// TODO: Split and refactor (in progress)

import qs from 'qs';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { animated, interpolate, useSpring } from 'react-spring';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { defaults } from 'lodash';
import { Signal } from 'signals';
import orderBy from 'lodash/orderBy';

import defaultCourtImage from '../../assets/images/placeholders/court.jpg';

import Spinner from '../../components/spinner/Spinner';
import Slider from '../../components/slider/Slider';
import PlayersFilter from '../../components/filters/players-filter/PlayersFilter';
import useResize from '../../hooks/useResize';
import { useLocalStore, useStore } from '../../stores';
import { menuIcon, profileAddIcon, profileIcon, searchIcon } from '../../utils/icons';
import { device, insets } from '../../utils/device';
import { clamp, lerp } from '../../utils/math';

import User from '../../stores/models/User';
import Court from '../../stores/models/Court';

import { AnimState } from './animations/animate';

import Maps from './components/maps/Maps';
import Content from './components/content/Content';
import Controls from './components/controls/Controls';
import Tabs from './components/tabs/Tabs';
import PlayersFilterCard from './components/filters/PlayersFilterCard';
import CourtsFilterCard from './components/filters/CourtsFilterCard';

import styles from './HomePage.module.scss';

import './HomePage.scss';
import ScheduleCalendar from '../../components/filters/players-filter/components/schedule-calendar/ScheduleCalendar';
import UserStore from '../../stores/UserStore';
import CourtStore from '../../stores/CourtStore';
import SwiperClass from 'swiper/types/swiper-class';

type Params = {
    page: string,
    id?: string,
};

type LocalStore = {
    isLoading: boolean,
    isProcess: boolean,
    error: string,

    history: any[],

    courts: Court[],
    users: User[],

    court: Court | null,
    user: User | null,

    slider: {
        prevPrev: {
            type: string,
            index: number,
            value: any,
            items: any[],
        },
        prev: {
            type: string,
            index: number,
            value: any,
            items: any[],
        },
        next: {
            type: string,
            index: number,
            value: any,
            items: any[],
        }
        curr: {
            type: string,
            initial: number,
            index: number,
            value: any,
            itemsRaw: any[],
            items: any[],
        },
    }

    filter: {
        open: boolean,
        mode: '' | 'filter' | 'sort' | 'manage',

        type: string[],

        players: {
            open: boolean,
            mode: '' | 'filter' | 'sort' | 'manage',
            type: 'players' | 'coaches',

            gender: string[],
            rating: {
                active: string[],

                age: number[],
                experience: number[],
                ntrp: number[],
                ello: number[],
            },
            preference: {
                court: string[],
                play: string[],
                training: string[],
            },
            calendar: {
                type: '' | 'schedule' | 'specific',

                schedule: { date: string, time: string[] }[],
                specific: { date: string, time: string[] }[],
            },
        }

        sort: {
            key: string,
            order: '' | 'asc' | 'desc',
        },
    },

    calendar: any,
};

let cacheCardY = 0;
const computeCardY = (force = false) => {
    if (cacheCardY && !force) {
        return cacheCardY;
    }

    const sliderContainerElement = document.querySelector<HTMLDivElement>('.slider-container');
    if (!sliderContainerElement) {
        return 0;
    }

    const w = window.innerWidth;
    const h = window.innerHeight;

    let tw = 375;
    let th = 560;
    let to = 10;
    if (!device.isShortScreen) {
        tw = 375;
        th = 640;
        to = 15;
    }

    const scale = Math.min(w / tw, h / th);

    const elementRect = sliderContainerElement.getBoundingClientRect();

    cacheCardY = (((elementRect.y / scale) / 1.25) - ((insets.top + 45) / 1.25)) - to;
    return cacheCardY;
};

let cacheCourtsCardY = 0;
const computeCourtsCardY = (force = false) => {
    if (cacheCourtsCardY && !force) {
        return cacheCourtsCardY;
    }

    const sliderContainerElement = document.querySelector<HTMLDivElement>('.slider-container');
    if (!sliderContainerElement) {
        return 0;
    }

    const w = window.innerWidth;
    const h = window.innerHeight;

    let tw = 375;
    let th = 560;
    let to = 25.5;
    if (!device.isShortScreen) {
        tw = 375;
        th = 640;
        to = 15.5;
    }

    const scale = Math.min(w / tw, h / th);

    const elementRect = sliderContainerElement.getBoundingClientRect();

    cacheCourtsCardY = ((elementRect.y / scale) / 1.25) - to;
    return cacheCourtsCardY;
};

const computeCourtsCardScale = () => (device.isShortScreen ? 1.87 : 1.5625);

const bottomDelta = 12;

export default observer(function HomePage() {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const viewportScaleRef = useRef<number>(1);
    const viewportHeightRef = useRef<number>(100);

    const proxySignalRef = useRef<Signal>();

    const backIndexRef = useRef(-1);

    const swiperCurrRef = useRef<SwiperClass>();

    const sliderImmediateRef = useRef(false);
    const sliderReleaseRef = useRef(false);

    const sliderPrevPrevRef = useRef<HTMLDivElement | null>(null);
    const sliderPrevRef = useRef<HTMLDivElement | null>(null);
    const sliderNextRef = useRef<HTMLDivElement | null>(null);
    const sliderCurrRef = useRef<HTMLDivElement | null>(null);

    const animStateRef = useRef<AnimState>(AnimState.None);
    const animRestRef = useRef(false);

    const history = useHistory();
    const location = useLocation();
    const params = useParams<Params>();
    const query = defaults(qs.parse(location.search, { ignoreQueryPrefix: true }), {
        calendar: '',
        favorites: 'players',
        mode: params.page === 'search' ? 'courts' : 'players',
    });

    const { authStore, userStore, courtStore } = useStore();
    const { localStore } = useLocalStore<LocalStore>({
        isLoading: true,
        isProcess: true,
        error: '',

        history: [],

        courts: [],
        users: [],

        court: null,
        user: null,

        slider: {
            prevPrev: {
                type: '',
                index: 0,
                value: null,
                items: [],
            },
            prev: {
                type: '',
                index: 0,
                value: null,
                items: [],
            },
            next: {
                type: '',
                index: 0,
                value: null,
                items: [],
            },
            curr: {
                type: '',
                initial: -1,
                index: 0,
                value: null,
                itemsRaw: [],
                items: [],
            },
        },

        filter: {
            open: false,
            mode: '',

            type: ['hard', 'clay', 'grass'],

            players: {
                open: false,
                mode: '',
                type: 'players',

                gender: ['male', 'female'],
                rating: {
                    active: ['age', 'ntrp'],

                    age: [10, 70],
                    experience: [0, 30],
                    ntrp: [1, 7],
                    ello: [1000, 3000],
                },
                preference: {
                    court: ['hard', 'clay', 'grass'],
                    play: ['single', 'double', 'tournament'],
                    training: ['individual', 'group', 'professional'],
                },
                calendar: {
                    type: '',

                    schedule: [],
                    specific: [],
                },
            },

            sort: {
                key: '',
                order: '',
            },
        },

        calendar: {},
    });

    const enableViewport = () => {
        if (viewportRef.current) {
            viewportRef.current.style.pointerEvents = '';
            viewportRef.current.style.touchAction = '';
        }

        if (sliderPrevRef.current) {
            sliderPrevRef.current.style.zIndex = '1';
        }
        if (sliderNextRef.current) {
            sliderNextRef.current.style.zIndex = '2';
        }
        if (sliderCurrRef.current) {
            sliderCurrRef.current.style.zIndex = '3';
        }
    };

    const disableViewport = (prevIndex = '1', nextIndex = '2', currIndex = '3') => {
        if (viewportRef.current) {
            viewportRef.current.style.pointerEvents = 'none';
            viewportRef.current.style.touchAction = 'none';
        }

        if (sliderPrevRef.current) {
            sliderPrevRef.current.style.zIndex = prevIndex;
        }
        if (sliderNextRef.current) {
            sliderNextRef.current.style.zIndex = nextIndex;
        }
        if (sliderCurrRef.current) {
            sliderCurrRef.current.style.zIndex = currIndex;
        }
    };

    /* */

    const animInit = {
        alpha: 0,

        config: {
            tension: 210,
            friction: 15,
            mass: 1.5,
            clamp: true,
            // duration: 3000,
        },

        immediate: false,

        onStart: () => {
            animRestRef.current = false;
        },

        onRest: () => proxySignalRef.current?.dispatch('anim-rest'),
    };
    const [anim, setAnim] = useSpring(() => animInit);
    const animUpdate = (alpha: number, immediate = false) => {
        setAnim({ alpha, immediate });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        Object.keys(anim).forEach(key => anim[key].flush());
    };

    /* */

    const handleAnimRest = () => {
        animRestRef.current = true;

        if (!sliderReleaseRef.current) {
            if (!sliderImmediateRef.current) {
                process.nextTick(() => enableViewport());
            }

            return;
        }

        sliderReleaseRef.current = false;

        if (animStateRef.current === AnimState.SearchToPlayers) {
            history.push(`/players/${localStore.slider.curr.value.id}`);
        }

        if (animStateRef.current === AnimState.SearchToCourts) {
            history.push(`/courts/${localStore.slider.curr.value.id}`);
        }

        if (animStateRef.current === AnimState.SearchToCalendarProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.SearchToCalendar));
        }

        if (animStateRef.current === AnimState.SearchToCalendar) {
            history.push({
                pathname: '/search',
                search: qs.stringify({
                    mode: query.mode,
                    calendar: localStore.slider.curr.value.id,
                }),
            });
        }

        if (animStateRef.current === AnimState.SearchCalendarToSearchProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.SearchCalendarToSearch));
        }

        if (animStateRef.current === AnimState.SearchCalendarToSearch) {
            history.push({
                pathname: '/search',
                search: qs.stringify({
                    mode: query.mode,
                }),
            });
        }

        if (animStateRef.current === AnimState.CourtsToSearch) {
            history.push('/search');
        }

        if (animStateRef.current === AnimState.CourtsToCalendarProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.CourtsToCalendar));
        }

        if (animStateRef.current === AnimState.CourtsToCalendar) {
            history.push({
                pathname: `/courts/${localStore.slider.prev.value.id}`,
                search: qs.stringify({
                    calendar: localStore.slider.curr.value.id,
                    mode: query.mode,
                }),
            });
        }

        if (animStateRef.current === AnimState.CourtsToPlayers) {
            history.push(`/players/${localStore.slider.curr.value.id}`);
        }

        if (animStateRef.current === AnimState.CourtsCalendarToCourtsProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.CourtsCalendarToCourts));
        }

        if (animStateRef.current === AnimState.CourtsCalendarToCourts) {
            history.push({
                pathname: `/courts/${localStore.slider.prev.value.id}`,
                search: qs.stringify({
                    mode: query.mode,
                }),
            });

            localStore.calendar.reset = true;
            process.nextTick(() => {
                localStore.calendar.reset = false;
            });
        }

        if (animStateRef.current === AnimState.PLayersPlayersToPlayers) {
            if (localStore.slider.curr.items.length) {
                localStore.history.push({
                    user: localStore.user,
                    items: [...localStore.slider.curr.items],
                });

                history.push(`/players/${localStore.slider.curr.value.id}`);
            }
        }

        if (animStateRef.current === AnimState.PLayersPlayersToPlayersBack) {
            if (localStore.slider.prevPrev.items.length) {
                history.push(`/players/${localStore.slider.prevPrev.items[0].id}`);
            }
        }

        if (animStateRef.current === AnimState.PlayersPlayersToCalendarProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.PlayersPlayersToCalendar));
        }

        if (animStateRef.current === AnimState.PlayersPlayersToCalendar) {
            history.push({
                pathname: `/players/${localStore.slider.prev.value.id}`,
                search: qs.stringify({
                    favorites: query.favorites,
                    calendar: localStore.slider.curr.value.id,
                }),
            });
        }

        if (animStateRef.current === AnimState.PlayersPlayersCalendarToPlayersPlayersProgress) {
            process.nextTick(() => handleSliderCurrSwipeDown(0, true, true, AnimState.PlayersPlayersCalendarToPlayersPlayers));
        }

        if (animStateRef.current === AnimState.PlayersPlayersCalendarToPlayersPlayers) {
            history.push({
                pathname: `/players/${localStore.slider.prev.value.id}`,
                search: qs.stringify({
                    favorites: query.favorites,
                }),
            });

            localStore.calendar.reset = true;
            process.nextTick(() => {
                localStore.calendar.reset = false;
            });
        }

        if (animStateRef.current === AnimState.PLayersPlayersToCourts) {
            history.push({
                pathname: `/courts/${localStore.court?.id || localStore.slider.prev.value.courts[0].id}`,
                search: qs.stringify({
                    mode: localStore.slider.prev.value.role,
                }),
            });
        }

        if (animStateRef.current === AnimState.PlayersCourtsToCourts) {
            history.push({
                pathname: `/courts/${localStore.slider.curr.value.id}`,
                search: qs.stringify({
                    mode: localStore.slider.prev.value.role,
                }),
            });
        }
    };

    /* */

    const handleSliderPrevSwipeDown = (delta: number, manual: boolean, release: boolean) => {
        // Select animation state
        if (params.page === 'courts') {
            animStateRef.current = AnimState.CourtsToSearch;
        } else if (params.page === 'players') {
            animStateRef.current = AnimState.PLayersPlayersToCourts;
        } else {
            return;
        }

        if (sliderReleaseRef.current) {
            return;
        }

        disableViewport('10');

        const height = -computeCardY();
        const alpha = +clamp(delta / height, 0, 1).toFixed(4);

        if (release && (manual || alpha >= 0.3)) {
            animUpdate(1, false);

            sliderImmediateRef.current = false;
            sliderReleaseRef.current = true;
        } else if (manual) {
            animUpdate(alpha, true);

            sliderImmediateRef.current = true;
        } else {
            animUpdate(animInit.alpha, false);

            sliderImmediateRef.current = false;
        }
    };

    const handleSliderCurrSwipeUp = (delta: number, manual: boolean, release: boolean) => {
        const type = localStore.slider.curr.type;
        const index = localStore.slider.curr.index;

        if (index === -1) {
            return;
        }

        // Select animation state
        if (params.page === 'search') {
            if (query.calendar) {
                return;
            }

            if (query.mode === 'courts') {
                animStateRef.current = AnimState.SearchToCourts;
            } else {
                animStateRef.current = AnimState.SearchToPlayers;
            }
        } else if (params.page === 'courts') {
            if (query.calendar) {
                return;
            }

            animStateRef.current = AnimState.CourtsToPlayers;
        } else if (params.page === 'players') {
            if (query.calendar) {
                return;
            }

            if (query.favorites === 'players') {
                animStateRef.current = AnimState.PLayersPlayersToPlayers;
            } else if (query.favorites === 'courts') {
                animStateRef.current = AnimState.PlayersCourtsToCourts;
            } else {
                return;
            }
        } else {
            return;
        }

        if (sliderReleaseRef.current) {
            return;
        }

        disableViewport();

        const height = type === 'courts' ? computeCourtsCardY() : computeCardY();
        const alpha = +clamp(delta / height, 0, 1).toFixed(4);

        if (release && (manual || alpha >= 0.3)) {
            animUpdate(1, false);

            sliderImmediateRef.current = false;
            sliderReleaseRef.current = true;
        } else if (manual) {
            animUpdate(alpha, true);

            sliderImmediateRef.current = true;
        } else {
            animUpdate(animInit.alpha, false);

            sliderImmediateRef.current = false;
        }
    };

    const handleSliderCurrSwipeDown = (delta: number, manual: boolean, release: boolean, state?: AnimState) => {
        const type = localStore.slider.curr.type;
        const index = localStore.slider.curr.index;

        if (index === -1) {
            return;
        }

        // Select animation state
        if (state) {
            animStateRef.current = state;
        } else if (params.page === 'search') {
            if (query.calendar) {
                animStateRef.current = AnimState.SearchCalendarToSearchProgress;
            } else {
                animStateRef.current = AnimState.SearchToCalendarProgress;
            }
        } else if (params.page === 'courts') {
            if (query.calendar) {
                animStateRef.current = AnimState.CourtsCalendarToCourtsProgress;
            } else {
                animStateRef.current = AnimState.CourtsToCalendarProgress;
            }
        } else if (params.page === 'players') {
            if (query.calendar) {
                animStateRef.current = AnimState.PlayersPlayersCalendarToPlayersPlayersProgress;
            } else {
                animStateRef.current = AnimState.PlayersPlayersToCalendarProgress;
            }
        } else {
            return;
        }

        if (sliderReleaseRef.current) {
            return;
        }

        disableViewport();

        const height = -bottomDelta;
        const alpha = +clamp(delta / height, 0, 1).toFixed(4);

        if (release && (manual || alpha >= 0.6)) {
            let value = 1;
            if (animStateRef.current === AnimState.SearchToCalendar
                || animStateRef.current === AnimState.SearchCalendarToSearch
                || animStateRef.current === AnimState.CourtsToCalendar
                || animStateRef.current === AnimState.CourtsCalendarToCourts
                || animStateRef.current === AnimState.PlayersPlayersToCalendar
                || animStateRef.current === AnimState.PlayersPlayersCalendarToPlayersPlayers) {
                value = 0;
            }

            sliderImmediateRef.current = false;
            sliderReleaseRef.current = true;

            animUpdate(value, false);
        } else if (manual) {
            sliderImmediateRef.current = true;

            animUpdate(alpha, true);
        } else {
            sliderImmediateRef.current = false;

            animUpdate(animInit.alpha, false);
        }
    };

    /* */

    // Page changed effect
    useEffect(() => {
        localStore.isProcess = true;

        Promise.all([
            localStore.users.length === 0 ? userStore.find() : Promise.resolve(localStore.users),
            localStore.courts.length === 0 ? courtStore.find() : Promise.resolve(localStore.courts),
            params.page === 'players' && params.id ? userStore.findById(params.id) : Promise.resolve(null),
            params.page === 'courts' && params.id ? courtStore.findById(params.id) : Promise.resolve(null),
        ])
            .then(([users, courts, user, court]) => runInAction(() => {
                // Select animation state
                if (params.page === 'search') {
                    if (query.calendar) {
                        animStateRef.current = AnimState.SearchToCalendar;
                    } else {
                        animStateRef.current = AnimState.Search;
                    }
                } else if (params.page === 'courts') {
                    if (query.calendar) {
                        animStateRef.current = AnimState.CourtsToCalendar;
                    } else {
                        animStateRef.current = AnimState.Courts;
                    }
                } else if (params.page === 'players') {
                    if (query.calendar) {
                        animStateRef.current = AnimState.PlayersPlayersToCalendar;
                    } else {
                        animStateRef.current = AnimState.Players;
                    }
                } else {
                    animStateRef.current = AnimState.None;
                }

                localStore.users = users.filter(u => ['players', 'coaches'].includes(u.role));
                localStore.courts = courts;

                if (params.page === 'search') {
                    localStore.slider.prev.type = '';
                    localStore.slider.prev.items = [];

                    localStore.slider.next.type = '';
                    localStore.slider.next.items = [];

                    if (query.mode === 'players') {
                        localStore.slider.curr.type = 'players';
                        localStore.slider.curr.itemsRaw = localStore.users.filter(u => u.role === 'players');
                        localStore.slider.curr.items = localStore.slider.curr.itemsRaw;
                    } else if (query.mode === 'coaches') {
                        localStore.slider.curr.type = 'players';
                        localStore.slider.curr.itemsRaw = localStore.users.filter(u => u.role === 'coaches');
                        localStore.slider.curr.items = localStore.slider.curr.itemsRaw;
                    } else if (query.mode === 'courts') {
                        localStore.slider.curr.type = 'courts';
                        localStore.slider.curr.itemsRaw = localStore.courts;
                        localStore.slider.curr.items = localStore.slider.curr.itemsRaw;
                    }
                }

                if (params.page === 'courts') {
                    if (court) {
                        const prevPlayer = localStore.slider.prev.type === 'players' && localStore.slider.prev.value
                            ? localStore.slider.prev.value
                            : null;

                        let prevPlayers: any[] = (court.players || [])
                            .map(player => localStore.users.find(p => p.id === player.id))
                            .filter(Boolean).filter(player => player?.id !== prevPlayer?.id);
                        if (prevPlayer) {
                            if (prevPlayers.length) {
                                prevPlayers = [
                                    ...prevPlayers.slice(0, device.isWideScreen ? 2 : 1),
                                    prevPlayer,
                                    ...prevPlayers.slice(device.isWideScreen ? 2 : 1),
                                ];
                            } else {
                                prevPlayers = [prevPlayer];
                            }
                        }
                        prevPlayers = prevPlayers
                            .filter((player: any, i, players: any[]) => players.findIndex(p => p.id === player.id) === i);

                        localStore.court = court;

                        localStore.courts = localStore.courts.length
                            ? [...localStore.courts.slice(0, 1), court, ...localStore.courts.slice(1)]
                            : [court];
                        localStore.courts = localStore.courts.filter((court, i, courts) => courts.findIndex(c => c.id === court.id) === i);

                        localStore.slider.prev.type = 'courts';
                        localStore.slider.prev.items = localStore.courts.slice(0, 3);

                        localStore.slider.next.type = '';
                        localStore.slider.next.items = [];

                        localStore.slider.curr.type = 'players';
                        localStore.slider.curr.itemsRaw = prevPlayers.filter((player: any) => player.role === (query.mode === 'coaches' ? 'coaches' : 'players'));
                        localStore.slider.curr.items = localStore.slider.curr.itemsRaw;
                    } else {
                        localStore.court = null;
                    }
                }

                if (params.page === 'players') {
                    if (user) {
                        let prevPlayers = (localStore.court?.players || user.courts[0]?.players || [])
                            .map(player => localStore.users.find(p => p.id === player.id))
                            .filter(Boolean)
                            .filter(player => player?.id !== user.id)
                            .concat(localStore.users.filter(u => u.id !== user.id).slice(0, 5));
                        if (prevPlayers.length) {
                            prevPlayers = [
                                ...prevPlayers.slice(0, device.isWideScreen ? 2 : 1),
                                user,
                                ...prevPlayers.slice(device.isWideScreen ? 2 : 1),
                            ];
                        } else {
                            prevPlayers = [user];
                        }
                        prevPlayers = prevPlayers
                            .filter((player: any, i, players: any[]) => players.findIndex(p => p.id === player.id) === i);

                        if (localStore.slider.prev.type === 'players') {
                            localStore.court = null;
                        }

                        localStore.slider.prev.type = 'players';
                        localStore.slider.prev.items = prevPlayers.slice(0, 5);

                        localStore.slider.next.type = '';
                        localStore.slider.next.items = [];

                        localStore.slider.curr.type = String(query.favorites || 'players');
                        localStore.slider.curr.itemsRaw = localStore.slider.curr.type === 'players'
                            ? user.friends
                                .concat(localStore.users.filter(u => u.id !== user.id).slice(0, 10))
                                .filter((item: any, i: number, items: any[]) => items.findIndex(d => d.id === item.id) === i)
                            : localStore.slider.curr.type === 'courts'
                                ? user.courts
                                    .concat(localStore.courts.slice(0, 10))
                                    .filter((item: any, i: number, items: any[]) => items.findIndex(d => d.id === item.id) === i)
                                : [];
                        if (localStore.slider.curr.type === 'players' && backIndexRef.current !== -1) {
                            const items = localStore.slider.curr.itemsRaw.filter((v: any) => v.id !== localStore.user?.id);
                            items.splice((device.isWideScreen ? 2 : 1), 0, localStore.user);
                            localStore.slider.curr.itemsRaw = items;

                            backIndexRef.current = -1;
                        }
                        localStore.slider.curr.items = localStore.slider.curr.itemsRaw;

                        localStore.user = user;
                    } else {
                        localStore.user = null;
                    }
                }

                localStore.isLoading = false;
                localStore.isProcess = false;
            }))
            .catch(() => runInAction(() => {
                localStore.isLoading = false;
                localStore.isProcess = true;
            }))
            .finally(() => animUpdate(animInit.alpha, true));
    }, [params.page, params.id, query.mode, query.favorites]);

    useEffect(() => runInAction(() => {
        localStore.slider.prevPrev.type = '';
        localStore.slider.prevPrev.items = [];

        if (params.page === 'search') {
            localStore.slider.next.type = 'players';
            localStore.slider.next.items = (localStore.slider.curr.value?.players || localStore.slider.curr.value?.friends || []).slice(0, 5);
        }

        if (params.page === 'courts') {
            localStore.slider.next.type = 'players';
            localStore.slider.next.items = (localStore.slider.curr.value?.friends || []).slice(0, 5);
        }

        if (params.page === 'players') {
            if (query.favorites === 'players') {
                const user = localStore.slider.curr.value;
                if (user) {
                    localStore.slider.next.type = 'players';
                    localStore.slider.next.items = user.friends
                        .concat(localStore.users.slice(0, 10))
                        .filter((item: any, i: number, items: any[]) => items.findIndex(d => d.id === item.id) === i);
                }
            } else if (query.favorites === 'courts') {
                const court = localStore.slider.curr.value;
                if (court) {
                    localStore.slider.next.type = 'players';
                    localStore.slider.next.items = court.players
                        .concat(localStore.users.slice(0, 10))
                        .filter((item: any, i: number, items: any[]) => items.findIndex(d => d.id === item.id) === i);
                }
            }
        }
    }), [localStore.slider.curr.value?.id]);

    useEffect(() => {
        if (!localStore.isProcess) {
            process.nextTick(() => enableViewport());
        }
    }, [localStore.isProcess, params.page, params.id, query.mode, query.calendar, query.favorites]);

    useResize(() => process.nextTick(() => {
        const w = window.innerWidth;
        const h = window.innerHeight;

        let tw = 375;
        let th = 560;
        if (!device.isShortScreen) {
            tw = 375;
            th = 640;
        }

        viewportScaleRef.current = Math.min(w / tw, h / th);
        viewportHeightRef.current = 100 / viewportScaleRef.current;

        // Select animation state
        if (params.page === 'search') {
            if (query.calendar) {
                animStateRef.current = AnimState.SearchToCalendar;
            } else {
                animStateRef.current = AnimState.Search;
            }
        } else if (params.page === 'courts') {
            if (query.calendar) {
                animStateRef.current = AnimState.CourtsToCalendar;
            } else {
                animStateRef.current = AnimState.Courts;
            }
        } else if (params.page === 'players') {
            if (query.calendar) {
                animStateRef.current = AnimState.PlayersPlayersToCalendar;
            } else {
                animStateRef.current = AnimState.Players;
            }
        } else {
            animStateRef.current = AnimState.None;
        }

        computeCardY(true);
        computeCourtsCardY(true);

        animUpdate(0, true);
    }), [params.page, query.calendar]);

    useEffect(() => {
        proxySignalRef.current = new Signal();
        proxySignalRef.current.add((type: string, ...args) => {
            if (type === 'anim-rest') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleAnimRest(...args);
            }
        });

        return () => {
            proxySignalRef.current?.dispose();
        };
    }, [handleAnimRest]);

    useEffect(() => {
        localStore.filter.players.calendar.schedule = authStore.user?.calendar || [];
    }, [authStore.user?.calendar]);

    if (localStore.isLoading) {
        return (
            <Spinner/>
        );
    }

    function computeInterpolate<T>(states: Record<number, T>): T {
        const value = states[animStateRef.current];
        return value != null ? value : states[AnimState.None];
    }

    function computeSliderPrevPrevCardStyle(i: number) {
        const style: any = {};

        const type = localStore.slider.prevPrev.type;
        const index = localStore.slider.prevPrev.index;

        if (i < index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${type === 'courts' ? -50 : -117}%, 0)`,
            }));
        }
        if (i === index) {
            if (type === 'players') {
                style.transformOrigin = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.PLayersPlayersToPlayersBack]: 'top center',
                }));
                style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.PLayersPlayersToPlayersBack]: `translate(0, ${lerp(0, -computeCardY() - 15, 1)}px) scale(${lerp(1, 1.2, alpha)})`,
                }));
            }

            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: 0,

                [AnimState.PLayersPlayersToPlayersBack]: alpha,
            }));
        }
        if (i > index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${type === 'courts' ? 50 : 117}%, 0)`,
            }));
        }
        if (i < index || i > index) {
            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: 0,
            }));
        }

        style.willChange = interpolate([anim.alpha], alpha => computeInterpolate({
            [AnimState.None]: alpha > 0 && alpha < 1 ? 'transform' : '',
        }));

        return style;
    }

    function computeSliderPrevCardStyle(i: number) {
        const style: any = {};

        const type = localStore.slider.prev.type;
        const index = localStore.slider.prev.index;

        if (i < index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${type === 'courts' ? -50 : -117}%, 0)`,

                [AnimState.CourtsToSearch]: `translate(${lerp(type === 'courts' ? -50 : -117, 0, alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(type === 'courts' ? -50 : -117, 0, alpha)}%, 0)`,
                [AnimState.PLayersPlayersToCourts]: `translate(${lerp(type === 'courts' ? -50 : -117, 0, alpha)}%, 0)`,
            }));
        }
        if (i === index) {
            if (type === 'players') {
                style.transformOrigin = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.PLayersPlayersToPlayers]: 'top center',
                    [AnimState.PLayersPlayersToPlayersBack]: 'top center',
                    [AnimState.PlayersCourtsToCourts]: 'top center',
                }));
                style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.Players]: `translate(0, ${lerp(0, -computeCardY(), 1)}px) scale(${lerp(1, 1.2, 1)})`,
                    [AnimState.PLayersPlayersToPlayers]: `translate(0, ${lerp(0, -computeCardY() - 15, 1)}px) scale(${lerp(1, 1.2, 1 - alpha)})`,
                    [AnimState.PLayersPlayersToPlayersBack]: `translate(0, ${lerp(0, -computeCardY() - 15, 1 - alpha)}px) scale(${lerp(1, 1.2, 1 - alpha)})`,
                    [AnimState.PLayersPlayersToCourts]: `translate(0, ${lerp(-computeCardY(), 0, alpha)}px) scale(${lerp(1.2, 1, alpha)})`,
                    [AnimState.PlayersPlayersToCalendarProgress]: `translate(0, ${lerp(0, -computeCardY(), 1)}px) scale(${lerp(1, 1.2, 1)})`,
                    [AnimState.PlayersPlayersToCalendar]: `translate(0, ${lerp(0, -computeCardY(), 1)}px) scale(${lerp(1, 1.2, 1)})`,
                    [AnimState.PlayersPlayersToFilter]: `translate(0, calc(${lerp(0, -computeCardY(), 1)}px - ${lerp(0, 150, alpha)}%)) scale(${lerp(1, 1.2, 1)})`,
                    [AnimState.PlayersCourtsToCourts]: `translate(0, ${lerp(0, -computeCardY() - 15, 1)}px) scale(${lerp(1, 1.2, 1 - alpha)})`,
                    [AnimState.PlayersCourtsToFilter]: `translate(0, calc(${lerp(0, -computeCardY(), 1)}px - ${lerp(0, 150, alpha)}%)) scale(${lerp(1, 1.2, 1)})`,

                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translate(0, ${lerp(0, -computeCardY(), 1)}px) scale(${lerp(1, 1.2, 1)})`,
                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(0, ${lerp(0, -computeCardY(), 1)}px) scale(${lerp(1, 1.2, 1)})`,
                }));
            }

            if (type === 'courts') {
                style.borderRadius = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.None]: '0 0 7px 7px',

                    [AnimState.CourtsToSearch]: `${lerp(0, 7, alpha)}px ${lerp(0, 7, alpha)}px 7px 7px`,
                    [AnimState.CourtsToFilter]: `0 0 ${lerp(7, 0, alpha)}px ${lerp(7, 0, alpha)}px`,

                    [AnimState.CourtsCalendar]: 0,
                }));
                style.boxShadow = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.None]: '0 2px 14px rgba(7, 44, 77, 0)',

                    [AnimState.CourtsToSearch]: `0 2px 14px rgba(7, 44, 77, ${alpha})`,
                }));
                style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.None]: `translate(0, ${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px) scale(${computeCourtsCardScale()})`,

                    [AnimState.CourtsToSearch]: `translate(0, ${lerp(device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25), 0, alpha)}px) scale(${lerp(computeCourtsCardScale(), 1, alpha)})`,
                    [AnimState.CourtsToCalendar]: `translate(0, calc(${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px - ${lerp(0, device.isShortScreen ? 50 : 50, 1 - alpha)}%)) scale(${computeCourtsCardScale()})`,
                    [AnimState.CourtsToFilter]: `translate(0, calc(${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px - ${lerp(0, device.isShortScreen ? 152 : 157, alpha)}%)) scale(${computeCourtsCardScale()})`,

                    [AnimState.CourtsCalendar]: `translate(0, calc(${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px - ${device.isShortScreen ? 152 : 157}%)) scale(${computeCourtsCardScale()})`,
                    [AnimState.CourtsCalendarToCourtsProgress]: `translate(0, calc(${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px - ${lerp(0, device.isShortScreen ? 50 : 50, 1 - alpha)}%)) scale(${computeCourtsCardScale()})`,
                    [AnimState.CourtsCalendarToCourts]: `translate(0, calc(${device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25)}px - ${lerp(0, device.isShortScreen ? 50 : 50, 0)}%)) scale(${computeCourtsCardScale()})`,

                    [AnimState.CourtsToPlayers]: `translate(0, ${-(computeCourtsCardY() - lerp(device.isShortScreen ? 0 : (insets.top + 45) / 1.25, (device.isStandalone ? 0 : -30) / 1.25, alpha))}px) scale(${computeCourtsCardScale()})`,
                }));
            }

            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.CourtsToPlayers]: 1 - alpha,

                [AnimState.PLayersPlayersToPlayers]: 1 - alpha,
                [AnimState.PLayersPlayersToPlayersBack]: 1,
                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                [AnimState.PlayersPlayersToCalendar]: 0,

                [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 0,
                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
            }));
        }
        if (i > index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${type === 'courts' ? 50 : 117}%, 0)`,

                [AnimState.CourtsToSearch]: `translate(${lerp(type === 'courts' ? 50 : 117, 0, alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(type === 'courts' ? 50 : 117, 0, alpha)}%, 0)`,
                [AnimState.PLayersPlayersToCourts]: `translate(${lerp(type === 'courts' ? 50 : 117, 0, alpha)}%, 0)`,
            }));
        }
        if (i < index || i > index) {
            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: 0,

                [AnimState.CourtsToSearch]: alpha,

                [AnimState.PLayersPlayersToPlayersBack]: alpha,
                [AnimState.PLayersPlayersToCourts]: alpha,
            }));
        }

        style.willChange = interpolate([anim.alpha], alpha => computeInterpolate({
            [AnimState.None]: alpha > 0 && alpha < 1 ? 'transform' : '',
        }));

        return style;
    }

    function computeSliderNextCardStyle(i: number) {
        const style: any = {};

        const type = localStore.slider.next.type;
        const index = localStore.slider.next.index;

        if (i < index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${117}%, 0)`,

                [AnimState.SearchToPlayers]: `translate(${lerp(117, 0, alpha)}%, 0)`,
                [AnimState.SearchToCourts]: `translate(${lerp(117, 0, alpha)}%, 0)`,

                [AnimState.CourtsToPlayers]: `translate(${lerp(117, 0, alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(117, 0, alpha)}%, 0)`,
                [AnimState.PlayersCourtsToCourts]: `translate(${lerp(117, 0, alpha)}%, 0)`,
            }));
        }
        if (i === index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: 'scale(1)',

                [AnimState.SearchToPlayers]: `scale(${lerp(0.8, 1, alpha)})`,
                [AnimState.SearchToCourts]: `scale(${lerp(0.8, 1, alpha)})`,

                [AnimState.CourtsToPlayers]: `scale(${lerp(0.8, 1, alpha)})`,

                [AnimState.PLayersPlayersToPlayers]: `scale(${lerp(0.8, 1, alpha)})`,
                [AnimState.PlayersCourtsToCourts]: `scale(${lerp(0.8, 1, alpha)})`,
            }));
        }
        if (i > index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.None]: `translate(${-117}%, 0)`,

                [AnimState.SearchToPlayers]: `translate(${lerp(-117, 0, alpha)}%, 0)`,
                [AnimState.SearchToCourts]: `translate(${lerp(-117, 0, alpha)}%, 0)`,

                [AnimState.CourtsToPlayers]: `translate(${lerp(-117, 0, alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(-117, 0, alpha)}%, 0)`,
                [AnimState.PlayersCourtsToCourts]: `translate(${lerp(-117, 0, alpha)}%, 0)`,
            }));
        }

        style.willChange = interpolate([anim.alpha], alpha => computeInterpolate({
            [AnimState.None]: alpha > 0 && alpha < 1 ? 'transform' : '',
        }));
        style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
            [AnimState.None]: 0,

            [AnimState.SearchToPlayers]: alpha,
            [AnimState.SearchToCourts]: alpha,

            [AnimState.CourtsToPlayers]: alpha,

            [AnimState.PLayersPlayersToPlayers]: alpha,
            [AnimState.PlayersCourtsToCourts]: alpha,
        }));

        return style;
    }

    function computeSliderCurrCardStyle(i: number) {
        const style: any = {};

        const type = localStore.slider.curr.type;
        const index = localStore.slider.curr.index;

        if (i < index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.SearchToPlayers]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.SearchToCourts]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,

                [AnimState.CourtsToSearch]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.CourtsToPlayers]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PLayersPlayersToCourts]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PlayersCourtsToCourts]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
            }));
        }
        if (i === index) {
            if (type === 'players') {
                style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.SearchToPlayers]: `translate(0, ${lerp(0, -computeCardY(), alpha)}px) scale(${lerp(1, 1.2, alpha)})`,
                    [AnimState.SearchToCalendarProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.SearchToCalendar]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.SearchCalendarToSearchProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.SearchCalendarToSearch]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.CourtsToSearch]: `scale(${lerp(1, 0.8, alpha)})`,
                    [AnimState.CourtsToCalendarProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.CourtsToCalendar]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.CourtsCalendarToCourtsProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.CourtsCalendarToCourts]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.CourtsToPlayers]: `translate(0, ${lerp(0, -computeCardY(), alpha)}px) scale(${lerp(1, 1.2, alpha)})`,

                    [AnimState.PLayersPlayersToPlayers]: `translate(0, ${lerp(0, -computeCardY(), alpha)}px) scale(${lerp(1, 1.2, alpha)})`,
                    [AnimState.PLayersPlayersToPlayersBack]: `translate(0, 0) scale(${lerp(1, 0.8, alpha)})`,
                    [AnimState.PLayersPlayersToCourts]: `scale(${lerp(1, 0.8, alpha)})`,
                    [AnimState.PlayersPlayersToCalendarProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.PlayersPlayersToCalendar]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                }));
            }

            if (type === 'courts') {
                style.borderRadius = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.SearchToCourts]: `${lerp(7, 0, alpha)}px ${lerp(7, 0, alpha)}px 7px 7px`,

                    [AnimState.PlayersCourtsToCourts]: `${lerp(7, 0, alpha)}px ${lerp(7, 0, alpha)}px 7px 7px`,
                }));
                style.boxShadow = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.SearchToCourts]: `0 2px 14px rgba(7, 44, 77, ${1 - alpha})`,

                    [AnimState.PlayersCourtsToCourts]: `0 2px 14px rgba(7, 44, 77, ${1 - alpha})`,
                }));
                style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                    [AnimState.SearchToCourts]: `translate(0, ${lerp(0, device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25), alpha)}px) scale(${lerp(1, computeCourtsCardScale(), alpha)})`,
                    [AnimState.SearchToCalendarProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.SearchToCalendar]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.SearchCalendarToSearchProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.SearchCalendarToSearch]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.PlayersCourtsToCourts]: `translate(0, ${lerp(0, device.isShortScreen ? -computeCourtsCardY() : -(computeCourtsCardY() - (insets.top + 45) / 1.25), alpha)}px) scale(${lerp(1, computeCourtsCardScale(), alpha)})`,

                    [AnimState.PLayersPlayersToPlayers]: `translate(0, ${lerp(0, -computeCardY(), alpha)}px) scale(${lerp(1, 1.2, alpha)})`,
                    [AnimState.PlayersPlayersToCalendarProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.PlayersPlayersToCalendar]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,

                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translate(0, ${lerp(0, bottomDelta, alpha)}px)`,
                }));
            }

            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.SearchToFilter]: 1 - alpha,

                [AnimState.CourtsToSearch]: 1 - (index === -1 ? clamp(alpha * 5, 0, 1) : alpha),
                [AnimState.CourtsToFilter]: 1 - alpha,

                [AnimState.PLayersPlayersToPlayersBack]: 1 - (index === -1 ? clamp(alpha * 5, 0, 1) : alpha),
                [AnimState.PLayersPlayersToCourts]: 1 - (index === -1 ? clamp(alpha * 5, 0, 1) : alpha),
                [AnimState.PlayersPlayersToFilter]: 1 - alpha,
            }));
        }
        if (i > index) {
            style.transform = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.SearchToPlayers]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.SearchToCourts]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,

                [AnimState.CourtsToSearch]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.CourtsToPlayers]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,

                [AnimState.PLayersPlayersToPlayers]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PLayersPlayersToPlayersBack]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PLayersPlayersToCourts]: `translate(${lerp(0, type === 'courts' ? -50 : -(device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
                [AnimState.PlayersCourtsToCourts]: `translate(${lerp(0, type === 'courts' ? 50 : (device.isShortScreen ? 124 : 117), alpha)}%, 0)`,
            }));
        }
        if (i < index || i > index) {
            style.opacity = interpolate([anim.alpha], alpha => computeInterpolate({
                [AnimState.SearchToPlayers]: 1 - alpha,
                [AnimState.SearchToCourts]: 1 - alpha,
                [AnimState.SearchToCalendarProgress]: lerp(1, 0.5, alpha),
                [AnimState.SearchToCalendar]: 0.5,
                [AnimState.SearchToFilter]: i > index ? 1 - alpha : 1,

                [AnimState.SearchCalendar]: 0.5,
                [AnimState.SearchCalendarToSearchProgress]: lerp(0.5, 1, alpha),
                [AnimState.SearchCalendarToSearch]: 1,

                [AnimState.CourtsToSearch]: 1 - alpha,
                [AnimState.CourtsToPlayers]: 1 - alpha,
                [AnimState.CourtsToCalendarProgress]: lerp(1, 0.5, alpha),
                [AnimState.CourtsToCalendar]: 0.5,
                [AnimState.CourtsToFilter]: 1 - alpha,

                [AnimState.CourtsCalendar]: 0.5,
                [AnimState.CourtsCalendarToCourtsProgress]: lerp(0.5, 1, alpha),
                [AnimState.CourtsCalendarToCourts]: 1,

                [AnimState.PLayersPlayersToPlayers]: 1 - alpha,
                [AnimState.PLayersPlayersToPlayersBack]: 1 - alpha,
                [AnimState.PLayersPlayersToCourts]: 1 - alpha,
                [AnimState.PlayersPlayersToCalendarProgress]: lerp(1, 0.5, alpha),
                [AnimState.PlayersPlayersToCalendar]: 0.5,
                [AnimState.PlayersPlayersToFilter]: 1 - alpha,
                [AnimState.PlayersCourtsToFilter]: i > index ? 1 - alpha : 1,

                [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 0.5,
                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: lerp(0.5, 1, 1 - alpha),

                [AnimState.PlayersCourtsToCourts]: 1 - alpha,
            }));
        }

        style.willChange = interpolate([anim.alpha], alpha => computeInterpolate({
            [AnimState.None]: alpha > 0 && alpha < 1 ? 'transform' : '',
        }));

        return style;
    }

    /* */

    function handlePlayersFilter(mode: '' | 'filter' | 'sort') {
        if (params.page === 'search') {
            animStateRef.current = AnimState.SearchToFilter;
        } else if (params.page === 'courts') {
            animStateRef.current = AnimState.CourtsToFilter;
        } else if (params.page === 'players') {
            animStateRef.current = AnimState.PlayersPlayersToFilter;
        }

        if (localStore.filter.players.open) {
            animUpdate(0, false);

            localStore.filter.players.open = false;
            localStore.filter.players.mode = '';
            localStore.filter.players.type = query.mode === 'coaches' ? 'coaches' : 'players';
        } else {
            animUpdate(1, false);

            localStore.filter.players.open = true;
            localStore.filter.players.mode = mode;
            localStore.filter.players.type = query.mode === 'coaches' ? 'coaches' : 'players';
        }
    }

    function handleCourtsFilter(mode: '' | 'filter' | 'sort') {
        if (params.page === 'search') {
            animStateRef.current = AnimState.SearchToFilter;
        } else if (params.page === 'players') {
            animStateRef.current = AnimState.PlayersCourtsToFilter;
        }

        if (localStore.filter.open) {
            animUpdate(0, false);

            localStore.filter.open = false;
            localStore.filter.mode = '';
        } else {
            animUpdate(1, false);

            localStore.filter.open = true;
            localStore.filter.mode = mode;
        }
    }

    function computeStatusMassage(type: 'prev' | 'next' | 'curr') {
        const currCalendar = localStore.slider[type].value?.calendar || [];
        const currCalendarTimeOrder: any[] = ['<12', '12-6', '>6'];
        const currCalendarTimeMap: any = {
            '<12': 'Morning',
            '12-6': 'Afternoon',
            '>6': 'Evening',
        };
        const currCalendarFirst = currCalendar.find((c: any) => moment(c.date).startOf('day').isSameOrAfter(moment().startOf('day')));
        const currCalendarFirstDate = currCalendarFirst?.date || '';
        const currCalendarFirstTime = (currCalendarFirst?.time || [])
            .slice()
            .sort((a: any, b: any) => {
                const ai = currCalendarTimeOrder.indexOf(a);
                const bi = currCalendarTimeOrder.indexOf(b);
                return ai - bi;
            });
        const currCalendarStatus = currCalendarFirstDate && currCalendarFirstTime.length
            ? <span>Ready to play: <strong>{currCalendarTimeMap[currCalendarFirstTime[0]]} {moment(currCalendarFirstDate).format('MMM DD')}</strong></span>
            : <span>Not ready to play now</span>;

        return currCalendarStatus;
    }

    return (
        <div className={styles.viewport} ref={viewportRef}>
            <div className="top">
                <div className="top-row">
                    <div className="indicators-container">
                        <animated.div
                            className="indicators-gradient"
                            style={{
                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.SearchToPlayers]: `translateY(${lerp(0, device.isStandalone ? -100 : -150, alpha)}%)`,

                                    [AnimState.CourtsToPlayers]: `translateY(${lerp(0, device.isStandalone ? -100 : -150, alpha)}%)`,

                                    [AnimState.Players]: `translateY(${lerp(0, -100, 1)}%)`,
                                    [AnimState.PLayersPlayersToPlayers]: `translateY(${lerp(0, -100, 1)}%)`,
                                    [AnimState.PLayersPlayersToPlayersBack]: `translateY(${lerp(0, -100, 1)}%)`,
                                    [AnimState.PLayersPlayersToCourts]: `translateY(${lerp(0, -100, 1 - alpha)}%)`,
                                    [AnimState.PlayersPlayersToCalendarProgress]: `translateY(${lerp(-100, 0, 1)}%)`,
                                    [AnimState.PlayersPlayersToCalendar]: `translateY(${lerp(-100, 0, 1)}%)`,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translateY(${lerp(-100, 0, alpha)}%)`,
                                })),
                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.CourtsToCalendar]: alpha,

                                    [AnimState.CourtsCalendar]: 0.75,
                                    [AnimState.CourtsCalendarToCourtsProgress]: alpha,
                                    [AnimState.CourtsCalendarToCourts]: 1,

                                    [AnimState.PlayersPlayersToCalendarProgress]: 0,
                                    [AnimState.PlayersPlayersToCalendar]: 0,
                                    [AnimState.PlayersPlayersToFilter]: lerp(0, 0.75, alpha),
                                    [AnimState.PlayersCourtsToCourts]: alpha,
                                    [AnimState.PlayersCourtsToFilter]: lerp(0, 0.75, alpha),

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 0,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 0,
                                })),
                                zIndex: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.None]: 1,

                                    [AnimState.CourtsToCalendarProgress]: 2, // alpha ? 2 : 1,
                                    [AnimState.CourtsToCalendar]: 2,

                                    [AnimState.CourtsCalendarToCourtsProgress]: 2, // alpha ? 2 : 1,
                                })),
                            }}
                        />

                        <div className="indicators-bar">
                            <div
                                className="indicators-bar-item"
                                onClick={() => {
                                    history.push('/search');
                                }}
                                role="presentation"
                            >
                                <div className="icon" style={{ backgroundImage: `url(${searchIcon})` }}/>
                            </div>

                            <div
                                className="indicators-bar-title"
                                role="presentation"
                                onClick={() => {
                                    backIndexRef.current = -1;

                                    if (params.page === 'courts') {
                                        handleSliderCurrSwipeUp(0, true, true);
                                    } else if (params.page === 'players') {
                                        const prev = localStore.history.pop();
                                        if (prev && prev.user && localStore.user) {
                                            const items = prev.items.filter((v: any) => v.id !== localStore.user?.id);
                                            items.splice((device.isWideScreen ? 2 : 1), 0, localStore.user);

                                            localStore.slider.prevPrev.type = 'players';
                                            localStore.slider.prevPrev.items = [
                                                prev.user,
                                                prev.user,
                                                prev.user,
                                                prev.user,
                                                prev.user,
                                            ];

                                            localStore.slider.prev.type = 'players';
                                            localStore.slider.prev.items = items;

                                            backIndexRef.current = device.isWideScreen ? 2 : 1;

                                            process.nextTick(() => {
                                                disableViewport('10');
                                                animStateRef.current = AnimState.PLayersPlayersToPlayersBack;
                                                animUpdate(1, false);
                                                sliderImmediateRef.current = false;
                                                sliderReleaseRef.current = true;
                                            });
                                        } else {
                                            handleSliderPrevSwipeDown(0, true, true);
                                        }
                                    }
                                }}
                            >
                                <animated.div
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                            [AnimState.None]: 0,

                                            [AnimState.SearchToCourts]: alpha,
                                            [AnimState.SearchToCalendarProgress]: alpha,
                                            [AnimState.SearchToCalendar]: 1,

                                            [AnimState.SearchCalendar]: 1,
                                            [AnimState.SearchCalendarToSearchProgress]: 1 - alpha,

                                            [AnimState.CourtsToCalendarProgress]: alpha,
                                            [AnimState.CourtsToCalendar]: 1,

                                            [AnimState.CourtsCalendar]: 1,
                                            [AnimState.CourtsCalendarToCourtsProgress]: 1 - alpha,
                                            [AnimState.CourtsCalendarToCourts]: 0,

                                            [AnimState.PlayersPlayersToCalendar]: 1,

                                            [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 1 - alpha,
                                        })),
                                    }}
                                >
                                    {localStore.slider.curr.value?.name}
                                </animated.div>

                                <animated.div
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                            [AnimState.None]: 0,

                                            [AnimState.Courts]: 1,
                                            [AnimState.CourtsToSearch]: 1 - alpha,
                                            [AnimState.CourtsCalendarToCourtsProgress]: alpha,
                                            [AnimState.CourtsCalendarToCourts]: 1,
                                            [AnimState.CourtsToFilter]: 1 - alpha,

                                            [AnimState.PLayersPlayersToCourts]: alpha,
                                        })),
                                    }}
                                >
                                    {localStore.court
                                        ? localStore.court.name
                                        : localStore.slider.prev.value?.courts
                                            ? localStore.slider.prev.value.courts[0]?.name
                                            : ''}
                                </animated.div>

                                <animated.div
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                            [AnimState.None]: 0,

                                            [AnimState.PlayersPlayersToCalendarProgress]: alpha,

                                            [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: alpha,
                                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: alpha,
                                        })),
                                    }}
                                >
                                    {localStore.slider.curr.value?.courts
                                        ? localStore.slider.curr.value.courts[0]?.name
                                        : ''}
                                </animated.div>

                                <animated.div
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                            [AnimState.None]: 0,

                                            [AnimState.SearchToFilter]: alpha,

                                            [AnimState.CourtsToFilter]: alpha,

                                            [AnimState.PlayersPlayersToFilter]: alpha,
                                            [AnimState.PlayersCourtsToFilter]: alpha,
                                        })),
                                    }}
                                >
                                    {localStore.filter.open ? 'Filter Courts' : 'Filter Players'}
                                </animated.div>

                                <animated.div
                                    style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                            [AnimState.None]: 0,

                                            [AnimState.Search]: 1,
                                            [AnimState.SearchToPlayers]: 1 - alpha,
                                            [AnimState.SearchToCourts]: 1 - alpha,
                                            [AnimState.SearchToCalendarProgress]: 1 - alpha,
                                            [AnimState.SearchToFilter]: 1 - alpha,

                                            [AnimState.SearchCalendarToSearchProgress]: alpha,
                                            [AnimState.SearchCalendarToSearch]: 1,

                                            [AnimState.CourtsToSearch]: alpha,
                                        })),
                                    }}
                                >
                                    {query.mode.charAt(0).toUpperCase() + query.mode.slice(1)} in <span>Kiev</span>
                                </animated.div>
                            </div>

                            <div
                                className="indicators-bar-item"
                                role="presentation"
                                onClick={() => {
                                    if (authStore.isAuthenticated && authStore.user) {
                                        if (authStore.user.role === 'courts') {
                                            if (params.id === authStore.user.courts[0].id) {
                                                history.push('/profile');
                                            } else {
                                                history.push(`/courts/${authStore.user.courts[0].id}`);
                                            }
                                        } else if (params.id === authStore.user.id) {
                                            history.push('/profile');
                                        } else {
                                            history.push(`/players/${authStore.user.id}`);
                                        }
                                    } else {
                                        history.push('/auth');
                                    }
                                }}
                            >
                                <div
                                    className="icon"
                                    style={{
                                        position: 'absolute',
                                        background: `url(${menuIcon}) no-repeat center / contain`,
                                        opacity: authStore.isAuthenticated && authStore.user
                                            ? (authStore.user.role === 'courts'
                                                ? ((localStore.slider.prev.value?.id || params.id) === authStore.user.courts[0].id ? 1 : 0)
                                                : ((localStore.slider.prev.value?.id || params.id) === authStore.user.id ? 1 : 0))
                                            : 0,
                                    }}
                                />
                                <div
                                    className="icon"
                                    style={{
                                        background: `url(${authStore.isAuthenticated ? profileIcon : profileAddIcon}) no-repeat center / contain`,
                                        opacity: authStore.isAuthenticated && authStore.user
                                            ? (authStore.user.role === 'courts'
                                                ? ((localStore.slider.prev.value?.id || params.id) === authStore.user.courts[0].id ? 0 : 1)
                                                : ((localStore.slider.prev.value?.id || params.id) === authStore.user.id ? 0 : 1))
                                            : 1,
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <Maps anim={anim} animStateRef={animStateRef}/>

                    {!!localStore.court && (
                        <animated.div
                            className="background-container bg-court"
                            style={{
                                backgroundImage: `url(${localStore.court?.image || defaultCourtImage})`,

                                borderRadius: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.PlayersPlayersToFilter]: `0 0 ${lerp(0, 14, 1 - alpha)}px ${lerp(0, 14, 1 - alpha)}px`,
                                    [AnimState.PlayersCourtsToFilter]: `0 0 ${lerp(0, 14, 1 - alpha)}px ${lerp(0, 14, 1 - alpha)}px`,
                                })),
                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.CourtsToPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), alpha)}px)`,

                                    [AnimState.Players]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersCourtsToCourts]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PLayersPlayersToPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PLayersPlayersToCourts]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1 - alpha)}px)`,
                                    [AnimState.PlayersPlayersToCalendarProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersToCalendar]: `translateY(calc(${device.isShortScreen ? 0 : 45 + insets.top}px - ${lerp(0, 100, 1)}%))`,
                                    [AnimState.PlayersPlayersToFilter]: `translateY(calc(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px - ${lerp(0, 68, alpha)}%))`,
                                    [AnimState.PlayersCourtsToFilter]: `translateY(calc(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px - ${lerp(0, 68, alpha)}%))`,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                })),
                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.None]: 0,

                                    [AnimState.CourtsToPlayers]: 1,

                                    [AnimState.Players]: 1,
                                    [AnimState.PLayersPlayersToPlayers]: 1,
                                    [AnimState.PLayersPlayersToPlayersBack]: 1,
                                    [AnimState.PLayersPlayersToCourts]: 1,
                                    [AnimState.PlayersPlayersToCalendarProgress]: 1 - alpha,
                                    [AnimState.PlayersPlayersToCalendar]: 0,
                                    [AnimState.PlayersPlayersToFilter]: 1,
                                    [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                    [AnimState.PlayersCourtsToFilter]: 1,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 1,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1 - alpha,
                                })),
                            }}
                        >
                            <animated.div
                                className="background-gradient"
                                style={{
                                    opacity: interpolate([anim.alpha], alpha => (
                                        animStateRef.current === AnimState.PLayersPlayersToCourts
                                            ? 1 - alpha
                                            : 1
                                    )),
                                }}
                            />
                        </animated.div>
                    )}

                    {!localStore.court && !!localStore.slider.prevPrev.value?.courts && (
                        <animated.div
                            className="background-container bg-prev-prev"
                            style={{
                                backgroundImage: `url(${localStore.slider.prevPrev.value?.courts[0]?.image || defaultCourtImage})`,

                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.PLayersPlayersToPlayersBack]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                })),
                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.None]: 0,

                                    [AnimState.PLayersPlayersToPlayersBack]: 1,
                                })),
                            }}
                            onClick={() => {
                                if (params.page === 'players') {
                                    handleSliderPrevSwipeDown(0, true, true);
                                }
                            }}
                            role="presentation"
                        >
                            <animated.div
                                className="background-gradient"
                                style={{
                                    opacity: interpolate([anim.alpha], alpha => (
                                        animStateRef.current === AnimState.PLayersPlayersToCourts
                                            ? 1 - alpha
                                            : 1
                                    )),
                                }}
                            />
                        </animated.div>
                    )}

                    {!localStore.court && !!localStore.slider.prev.value?.courts && (
                        <animated.div
                            className="background-container bg-prev"
                            style={{
                                backgroundImage: `url(${localStore.slider.prev.value.courts[0]?.image || defaultCourtImage})`,

                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.Players]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersCourtsToCourts]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PLayersPlayersToPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PLayersPlayersToPlayersBack]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PLayersPlayersToCourts]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1 - alpha)}px)`,
                                    [AnimState.PlayersPlayersToCalendarProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersToCalendar]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersToFilter]: `translateY(calc(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px - ${lerp(0, 68, alpha)}%))`,
                                    [AnimState.PlayersCourtsToFilter]: `translateY(calc(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px - ${lerp(0, 68, alpha)}%))`,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                })),
                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.None]: 0,

                                    [AnimState.Players]: 1,
                                    [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                    [AnimState.PLayersPlayersToPlayers]: 1,
                                    [AnimState.PLayersPlayersToPlayersBack]: 1 - alpha,
                                    [AnimState.PLayersPlayersToCourts]: 1,
                                    [AnimState.PlayersPlayersToCalendarProgress]: 1, // 1 - alpha,
                                    [AnimState.PlayersPlayersToCalendar]: 1,
                                    [AnimState.PlayersPlayersToFilter]: 1,
                                    [AnimState.PlayersCourtsToFilter]: 1,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 1,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 1,
                                })),
                            }}
                            onClick={() => {
                                if (params.page === 'players') {
                                    handleSliderPrevSwipeDown(0, true, true);
                                }
                            }}
                            role="presentation"
                        >
                            <animated.div
                                className="background-gradient"
                                style={{
                                    opacity: interpolate([anim.alpha], alpha => (
                                        animStateRef.current === AnimState.PLayersPlayersToCourts
                                            ? 1 - alpha
                                            : 1
                                    )),
                                }}
                            />
                        </animated.div>
                    )}

                    {(!!localStore.slider.curr.value?.courts || !!localStore.slider.curr.value?.image) && (
                        <animated.div
                            className="background-container bg-curr"
                            style={{
                                backgroundImage: `url(${(localStore.slider.curr.value?.courts && localStore.slider.curr.value.courts[0].image) || localStore.slider.curr.value.image || defaultCourtImage})`,

                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.SearchToPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), alpha)}px)`,

                                    [AnimState.SearchToCalendarProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 0)}px)`,
                                    [AnimState.SearchToCalendar]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1 - alpha)}px)`,

                                    [AnimState.SearchCalendarToSearchProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1 - alpha)}px)`,
                                    [AnimState.SearchCalendarToSearch]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 0)}px)`,

                                    [AnimState.PLayersPlayersToPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersToCalendarProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersToCalendar]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: `translateY(${lerp(device.isShortScreen ? 0 : 45 + insets.top, (device.isStandalone ? 0 : -30), 1)}px)`,
                                })),
                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                    [AnimState.None]: 0,

                                    [AnimState.SearchToPlayers]: alpha,

                                    [AnimState.SearchToCalendarProgress]: alpha,
                                    [AnimState.SearchToCalendar]: 1,

                                    [AnimState.SearchCalendarToSearchProgress]: 1 - alpha,
                                    [AnimState.SearchCalendarToSearch]: 0,

                                    [AnimState.PLayersPlayersToPlayers]: alpha,
                                    [AnimState.PlayersPlayersToCalendarProgress]: alpha,
                                    [AnimState.PlayersPlayersToCalendar]: 1,

                                    [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 1,
                                    [AnimState.PlayersPlayersCalendarToPlayersPlayers]: alpha,
                                })),
                            }}
                            onClick={() => {
                                if (params.page === 'players') {
                                    handleSliderPrevSwipeDown(0, true, true);
                                }
                            }}
                            role="presentation"
                        >
                            <div className="background-gradient"/>
                        </animated.div>
                    )}
                </div>

                <div className="top-last-row">
                    <Controls page={params.page} query={query} item={localStore.slider.prev} anim={anim} animStateRef={animStateRef}/>
                </div>

                <animated.div
                    className="top-content"
                    style={{
                        height: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.None]: '100%',

                            [AnimState.CourtsToFilter]: localStore.filter.players.mode === 'filter' && alpha > 0
                                ? `${(window.innerHeight / viewportScaleRef.current) - (insets.top + 45)}px`
                                : '100%',

                            [AnimState.PlayersPlayersToFilter]: localStore.filter.players.mode === 'filter' && alpha > 0
                                ? `${(window.innerHeight / viewportScaleRef.current) - (insets.top + 45)}px`
                                : '100%',
                        })),
                        opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.None]: 0,

                            [AnimState.SearchToFilter]: alpha,

                            [AnimState.SearchToCalendarProgress]: 0,
                            [AnimState.SearchToCalendar]: 1 - alpha,

                            [AnimState.SearchCalendarToSearchProgress]: 1 - alpha,
                            [AnimState.SearchCalendarToSearch]: 0,
                            [AnimState.SearchCalendar]: 1,

                            [AnimState.CourtsToFilter]: alpha,

                            [AnimState.CourtsToCalendarProgress]: 0,
                            [AnimState.CourtsToCalendar]: 1 - alpha,

                            [AnimState.CourtsCalendar]: 1,
                            [AnimState.CourtsCalendarToCourtsProgress]: 1 - alpha,
                            [AnimState.CourtsCalendarToCourts]: 0,

                            [AnimState.PlayersPlayersToFilter]: alpha,
                            [AnimState.PlayersCourtsToFilter]: alpha,

                            [AnimState.PlayersPlayersToCalendarProgress]: 0,
                            [AnimState.PlayersPlayersToCalendar]: 1 - alpha,

                            [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 1,
                            [AnimState.PlayersPlayersCalendarToPlayersPlayers]: alpha,
                        })),
                        zIndex: interpolate([anim.alpha], alpha => computeInterpolate({
                            [AnimState.None]: -1,

                            [AnimState.SearchToCalendarProgress]: -1,
                            [AnimState.SearchToCalendar]: 10,
                            [AnimState.SearchToFilter]: alpha ? 10 : -1,

                            [AnimState.SearchCalendar]: 10,
                            [AnimState.SearchCalendarToSearchProgress]: -1,
                            [AnimState.SearchCalendarToSearch]: alpha ? 10 : -1,

                            [AnimState.CourtsToCalendarProgress]: -1,
                            [AnimState.CourtsToCalendar]: 10,
                            [AnimState.CourtsToFilter]: alpha ? 10 : -1,

                            [AnimState.CourtsCalendar]: 10,
                            [AnimState.CourtsCalendarToCourtsProgress]: -1,
                            [AnimState.CourtsCalendarToCourts]: alpha ? 10 : -1,

                            [AnimState.PlayersPlayersToFilter]: alpha ? 10 : -1,
                            [AnimState.PlayersCourtsToFilter]: alpha ? 10 : -1,

                            [AnimState.PlayersPlayersToCalendarProgress]: alpha > 0 ? 10 : -1,
                            [AnimState.PlayersPlayersToCalendar]: alpha < 1 ? 10 : -1,
                        })),
                    }}
                >
                    <animated.div
                        style={{
                            display: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: 'none',

                                [AnimState.SearchToCalendarProgress]: 'block',
                                [AnimState.SearchToCalendar]: 'block',

                                [AnimState.SearchCalendar]: 'block',
                                [AnimState.SearchCalendarToSearchProgress]: 'block',
                                [AnimState.SearchCalendarToSearch]: 'block',

                                [AnimState.CourtsToCalendarProgress]: 'block',
                                [AnimState.CourtsToCalendar]: 'block',

                                [AnimState.CourtsCalendar]: 'block',
                                [AnimState.CourtsCalendarToCourtsProgress]: 'block',
                                [AnimState.CourtsCalendarToCourts]: 'block',

                                [AnimState.PlayersPlayersToCalendarProgress]: 'block',
                                [AnimState.PlayersPlayersToCalendar]: 'block',

                                [AnimState.PlayersPlayersCalendarToPlayersPlayersProgress]: 'block',
                                [AnimState.PlayersPlayersCalendarToPlayersPlayers]: 'block',
                            })),
                            position: 'absolute',
                            marginTop: 5,
                            transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                [AnimState.None]: device.isShortScreen ? 'translateY(90%)' : 'translateY(113%)',
                            })),
                        }}
                    >
                        <ScheduleCalendar
                            title={{
                                schedule: 'My schedule',
                                specific:
                                    query.mode !== 'courts' && query.favorites === 'players'
                                        ? (localStore.slider.curr.value?.role === 'players'
                                            ? '"Player" schedule'
                                            : localStore.slider.curr.value?.role === 'coaches'
                                                ? '"Coach" schedule'
                                                : 'Target schedule')
                                        : '"Court" schedule',
                            }}
                            calendar={{
                                type: localStore.calendar.type || 'specific',
                                reset: localStore.calendar.reset,

                                schedule: (authStore.user?.role === 'courts' ? authStore.user.courts[0]?.calendar : authStore.user?.calendar)?.map((calendar: any) => ({
                                    date: moment(calendar.date).format('MMM DD'),
                                    time: calendar.time,
                                })) || [],
                                specific: localStore.slider.curr.value?.calendar?.map((calendar: any) => ({
                                    date: moment(calendar.date).format('MMM DD'),
                                    time: calendar.time,
                                })) || [],
                            }}
                            onChange={(calendar) => {
                                localStore.calendar = calendar;

                                if (authStore.user) {
                                    if (authStore.user.role === 'courts') {
                                        const court = authStore.user.courts[0];
                                        if (court) {
                                            court.calendar = calendar.schedule;

                                            CourtStore.saveById(court.id, court);
                                        }
                                    } else {
                                        authStore.user.calendar = calendar.schedule;

                                        UserStore.saveById(authStore.user.id, authStore.user);
                                    }
                                }

                                // if (localStore.slider.curr.value?.calendar) {
                                //     localStore.slider.curr.value.calendar = calendar.specific;
                                // }
                            }}
                        />
                    </animated.div>

                    {localStore.filter.open && localStore.filter.mode === 'filter' && (
                        <div className="filter-container">
                            <div className="filter-services">
                                <h3>Has additional services</h3>

                                <div className="filter-services-container">
                                    <div className="filter-services-row">
                                        <div className="filter-services-col">Racket rental</div>
                                        <div className="filter-services-col">Ball basket rental</div>
                                        <div className="filter-services-col">Ball machine rental</div>
                                    </div>
                                    <div className="filter-services-row">
                                        <div className="filter-services-col">Free parking</div>
                                        <div className="filter-services-col">Strings hauling</div>
                                        <div className="filter-services-col">Physiotherapy</div>
                                    </div>
                                    <div className="filter-services-row">
                                        <div className="filter-services-col">Gym / Cardio</div>
                                        <div className="filter-services-col">Classes for kids</div>
                                        <div className="filter-services-col">SPA / Pool</div>
                                    </div>
                                </div>
                            </div>

                            <div className="filter-types">
                                <h3>Type of courts</h3>

                                <div className="filter-types-container">
                                    <div className="filter-types-row">
                                        <div
                                            className="filter-types-col"
                                            style={{
                                                backgroundImage: `url(${require('../../assets/images/courts/types/hard.png').default})`,
                                                opacity: localStore.filter.type.includes('hard') ? 1 : 0.75,
                                            }}
                                            onClick={() => {
                                                if (localStore.filter.type.includes('hard')) {
                                                    if (localStore.filter.type.length > 1) {
                                                        localStore.filter.type = localStore.filter.type.filter(type => type !== 'hard');
                                                    }
                                                } else {
                                                    localStore.filter.type.push('hard');
                                                }
                                            }}
                                            role="presentation"
                                        >
                                            <div>Hard</div>
                                            <div>{localStore.courts.reduce((sum, court) => (sum + court.type.hard), 0)} Courts Available</div>
                                        </div>
                                        <div
                                            className="filter-types-col"
                                            style={{
                                                backgroundImage: `url(${require('../../assets/images/courts/types/clay.png').default})`,
                                                opacity: localStore.filter.type.includes('clay') ? 1 : 0.75,
                                            }}
                                            onClick={() => {
                                                if (localStore.filter.type.includes('clay')) {
                                                    if (localStore.filter.type.length > 1) {
                                                        localStore.filter.type = localStore.filter.type.filter(type => type !== 'clay');
                                                    }
                                                } else {
                                                    localStore.filter.type.push('clay');
                                                }
                                            }}
                                            role="presentation"
                                        >
                                            <div>Clay</div>
                                            <div>{localStore.courts.reduce((sum, court) => (sum + court.type.clay), 0)}  Courts Available</div>
                                        </div>
                                        <div
                                            className="filter-types-col"
                                            style={{
                                                backgroundImage: `url(${require('../../assets/images/courts/types/grass.png').default})`,
                                                opacity: localStore.filter.type.includes('grass') ? 1 : 0.75,
                                            }}
                                            onClick={() => {
                                                if (localStore.filter.type.includes('grass')) {
                                                    if (localStore.filter.type.length > 1) {
                                                        localStore.filter.type = localStore.filter.type.filter(type => type !== 'grass');
                                                    }
                                                } else {
                                                    localStore.filter.type.push('grass');
                                                }
                                            }}
                                            role="presentation"
                                        >
                                            <div>Grass</div>
                                            <div>{localStore.courts.reduce((sum, court) => (sum + court.type.grass), 0)}  Courts Available</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {localStore.filter.players.open && localStore.filter.players.mode === 'filter' && (
                        <div style={{ paddingTop: 10 }}>
                            <PlayersFilter
                                filter={toJS(localStore.filter.players)}
                                onChange={(filter) => {
                                    localStore.filter.players = filter;
                                }}
                            />
                        </div>
                    )}

                    {localStore.filter.open && localStore.filter.mode === 'sort' && (
                        <div className="filter-container">
                            <div className="filter-services">
                                <div className="filter-services-container">
                                    <div className="filter-services-row">
                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'players';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Players
                                            {localStore.filter.sort.key === 'players' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>

                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'coaches';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Coaches
                                            {localStore.filter.sort.key === 'coaches' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>

                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'courts';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Courts
                                            {localStore.filter.sort.key === 'courts' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>
                                    </div>
                                    <div className="filter-services-row">
                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'time';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            First Time Available
                                            {localStore.filter.sort.key === 'time' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {localStore.filter.players.open && localStore.filter.players.mode === 'sort' && (
                        <div className="filter-container">
                            <div className="filter-services">
                                <div className="filter-services-container">
                                    <div className="filter-services-row">
                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'age';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Age
                                            {localStore.filter.sort.key === 'age' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>

                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'rating';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Rating
                                            {localStore.filter.sort.key === 'rating' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>

                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'games';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            Games
                                            {localStore.filter.sort.key === 'games' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>
                                    </div>
                                    <div className="filter-services-row">
                                        <div
                                            className="filter-services-col"
                                            onClick={() => {
                                                localStore.filter.sort.key = 'time';
                                                if (localStore.filter.sort.order === '') {
                                                    localStore.filter.sort.order = 'asc';
                                                } else if (localStore.filter.sort.order === 'asc') {
                                                    localStore.filter.sort.order = 'desc';
                                                } else {
                                                    localStore.filter.sort.order = '';
                                                }
                                            }}
                                        >
                                            First Time Available
                                            {localStore.filter.sort.key === 'time' && localStore.filter.sort.order !== ''
                                                ? (localStore.filter.sort.order === 'asc'
                                                    ? <span style={{ marginLeft: 10 }}>&#x2193;</span>
                                                    : <span style={{ marginLeft: 10 }}>&#x2191;</span>)
                                                : <span style={{ marginLeft: 10, padding: '0 2px' }}>-</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </animated.div>
            </div>

            <div className="bottom">
                <div className="slider-container">
                    {!!localStore.slider.prevPrev.type && !!localStore.slider.prevPrev.items.length && (
                        <div className="slider-prev-prev" style={{ zIndex: 1 }} ref={sliderPrevPrevRef}>
                            <Slider
                                className={`slider-${localStore.slider.prevPrev.type}`}
                                name="prev-prev"
                                type={localStore.slider.prevPrev.type}
                                readonly
                                initialIndex={localStore.slider.prevPrev.type === 'courts' ? 1 : (device.isWideScreen ? 2 : 1)}
                                onChange={activeIndex => runInAction(() => {
                                    localStore.slider.prevPrev.index = activeIndex;
                                    localStore.slider.prevPrev.value = localStore.slider.prevPrev.items[localStore.slider.prevPrev.index];
                                })}
                            >
                                {localStore.slider.prevPrev.type === 'players'
                                    ? localStore.slider.prevPrev.items.map((player, i) => (
                                        <animated.div
                                            key={player.id + Math.random().toString()}
                                            className="card"
                                            style={{
                                                ...computeSliderPrevPrevCardStyle(i),
                                                backgroundImage: `url(${player.image})`,
                                            }}
                                        >
                                            <div className="header-block">
                                                <animated.div
                                                    className="header-container"
                                                    style={{
                                                        ...i === localStore.slider.prevPrev.index
                                                            ? {
                                                                color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 'rgb(255, 255, 255)',

                                                                    [AnimState.PLayersPlayersToCourts]: `rgb(${lerp(7, 255, 1 - alpha)}, ${lerp(44, 255, 1 - alpha)}, ${lerp(77, 255, 1 - alpha)})`,
                                                                })),
                                                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 'translateY(-5.5px) scale(0.94)',

                                                                    [AnimState.PLayersPlayersToCourts]: `translateY(${lerp(-5.5, 0, alpha)}px) scale(${lerp(0.94, 1, alpha)})`,
                                                                })),
                                                            }
                                                            : {},
                                                    }}
                                                >
                                                    {player.name}
                                                </animated.div>
                                            </div>

                                            <animated.div
                                                className="text-block"
                                                style={{
                                                    ...i === localStore.slider.prevPrev.index
                                                        ? {
                                                            font: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                [AnimState.None]: 'normal normal normal 9px/9px Copperplate',

                                                                [AnimState.PLayersPlayersToCourts]: `normal normal normal ${lerp(9, 10, alpha)}px/${lerp(9, 10, alpha)}px Copperplate`,
                                                            })),
                                                        }
                                                        : {},
                                                }}
                                            >
                                                <div className="text-container">
                                                    <div className="upper-field">
                                                        Rating: {player.ratings.ntrp}
                                                    </div>
                                                    <div className="lower-field">
                                                        Games: {player.totalGames}
                                                    </div>
                                                </div>
                                            </animated.div>
                                        </animated.div>
                                    ))
                                    : localStore.slider.prevPrev.type === 'courts'
                                        ? localStore.slider.prevPrev.items.map((court, i) => (
                                            <animated.div
                                                key={court.id + Math.random().toString()}
                                                className="court-card"
                                                style={{
                                                    ...computeSliderPrevPrevCardStyle(i),
                                                    backgroundImage: `url(${court.image})`,
                                                }}
                                            >
                                                <div className="header-block">
                                                    <animated.div
                                                        className="header-container"
                                                        style={{
                                                            ...i === localStore.slider.prevPrev.index
                                                                ? {
                                                                    color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: 'rgb(255, 255, 255)',

                                                                        [AnimState.CourtsToSearch]: `rgb(${lerp(7, 255, 1 - alpha)}, ${lerp(44, 255, 1 - alpha)}, ${lerp(77, 255, 1 - alpha)})`,
                                                                    })),
                                                                    transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: device.isShortScreen
                                                                            ? 'translateY(0) scale(0.59)'
                                                                            : 'translateY(-2px) scale(0.7)',

                                                                        [AnimState.CourtsToSearch]: device.isShortScreen
                                                                            ? `translateY(0) scale(${lerp(0.59, 1, alpha)})`
                                                                            : `translateY(${lerp(-2, 0, alpha)}px) scale(${lerp(0.7, 1, alpha)})`,
                                                                    })),
                                                                    opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: 1,

                                                                        [AnimState.CourtsToSearch]: alpha,
                                                                    })),
                                                                }
                                                                : {},
                                                        }}
                                                    >
                                                        {court.name}
                                                    </animated.div>
                                                </div>

                                                <animated.div
                                                    className="text-block"
                                                    style={{
                                                        ...i === localStore.slider.prevPrev.index
                                                            ? {
                                                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 0,

                                                                    [AnimState.CourtsToSearch]: alpha,
                                                                })),
                                                            }
                                                            : {},
                                                    }}
                                                >
                                                    <div className="text-container">
                                                        <div className="upper-field">
                                                            {court.address}
                                                        </div>
                                                        <div className="lower-field">
                                                            {court.city}, {court.country}
                                                        </div>
                                                    </div>
                                                </animated.div>
                                            </animated.div>
                                        ))
                                        : false}
                            </Slider>
                        </div>
                    )}

                    {!!localStore.slider.prev.type && !!localStore.slider.prev.items.length && (
                        <div className="slider-prev" style={{ zIndex: 1 }} ref={sliderPrevRef}>
                            <Slider
                                className={`slider-${localStore.slider.prev.type}`}
                                name="prev"
                                type={localStore.slider.prev.type}
                                onlyVertical
                                initialIndex={localStore.slider.prev.type === 'courts' ? 1 : (device.isWideScreen ? 2 : 1)}
                                onSwipeDownMove={handleSliderPrevSwipeDown}
                                onChange={activeIndex => runInAction(() => {
                                    localStore.slider.prev.index = activeIndex;
                                    localStore.slider.prev.value = localStore.slider.prev.items[localStore.slider.prev.index];
                                })}
                                onClick={(activeIndex) => {
                                    if (localStore.slider.prev.index === activeIndex) {
                                        handleSliderPrevSwipeDown(0, true, true);
                                    }
                                }}
                            >
                                {localStore.slider.prev.type === 'players'
                                    ? localStore.slider.prev.items.map((player, i) => (
                                        <animated.div
                                            key={player.id}
                                            className="card"
                                            style={{
                                                ...computeSliderPrevCardStyle(i),
                                                backgroundImage: `url(${player.image})`,
                                            }}
                                        >
                                            <div className="header-block">
                                                <animated.div
                                                    className="header-container"
                                                    style={{
                                                        ...i === localStore.slider.prev.index
                                                            ? {
                                                                color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 'rgb(255, 255, 255)',

                                                                    [AnimState.PLayersPlayersToPlayersBack]: `rgb(${lerp(7, 255, 1 - alpha)}, ${lerp(44, 255, 1 - alpha)}, ${lerp(77, 255, 1 - alpha)})`,
                                                                    [AnimState.PLayersPlayersToCourts]: `rgb(${lerp(7, 255, 1 - alpha)}, ${lerp(44, 255, 1 - alpha)}, ${lerp(77, 255, 1 - alpha)})`,
                                                                })),
                                                                transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 'translateY(-5.5px) scale(0.94)',

                                                                    [AnimState.PLayersPlayersToPlayersBack]: `translateY(${lerp(-5.5, 0, alpha)}px) scale(${lerp(0.94, 1, alpha)})`,
                                                                    [AnimState.PLayersPlayersToCourts]: `translateY(${lerp(-5.5, 0, alpha)}px) scale(${lerp(0.94, 1, alpha)})`,
                                                                })),
                                                            }
                                                            : {},
                                                    }}
                                                >
                                                    {player.name}
                                                </animated.div>
                                            </div>

                                            <animated.div
                                                className="text-block"
                                                style={{
                                                    ...i === localStore.slider.prev.index
                                                        ? {
                                                            font: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                [AnimState.None]: 'normal normal normal 9px/9px Copperplate',

                                                                [AnimState.PLayersPlayersToPlayersBack]: `normal normal normal ${lerp(9, 10, alpha)}px/${lerp(9, 10, alpha)}px Copperplate`,
                                                                [AnimState.PLayersPlayersToCourts]: `normal normal normal ${lerp(9, 10, alpha)}px/${lerp(9, 10, alpha)}px Copperplate`,
                                                            })),
                                                        }
                                                        : {},
                                                }}
                                            >
                                                <div className="text-container">
                                                    <div className="upper-field">
                                                        Rating: {player.ratings.ntrp}
                                                    </div>
                                                    <div className="lower-field">
                                                        Games: {player.totalGames}
                                                    </div>
                                                </div>
                                            </animated.div>
                                        </animated.div>
                                    ))
                                    : localStore.slider.prev.type === 'courts'
                                        ? localStore.slider.prev.items.map((court, i) => (
                                            <animated.div
                                                key={court.id}
                                                className="court-card"
                                                style={{
                                                    ...computeSliderPrevCardStyle(i),
                                                    backgroundImage: `url(${court.image})`,
                                                }}
                                            >
                                                <div className="header-block">
                                                    <animated.div
                                                        className="header-container"
                                                        style={{
                                                            ...i === localStore.slider.prev.index
                                                                ? {
                                                                    color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: 'rgb(255, 255, 255)',

                                                                        [AnimState.CourtsToSearch]: `rgb(${lerp(7, 255, 1 - alpha)}, ${lerp(44, 255, 1 - alpha)}, ${lerp(77, 255, 1 - alpha)})`,
                                                                    })),
                                                                    transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: device.isShortScreen
                                                                            ? 'translateY(0) scale(0.59)'
                                                                            : 'translateY(-2px) scale(0.7)',

                                                                        [AnimState.CourtsToSearch]: device.isShortScreen
                                                                            ? `translateY(0) scale(${lerp(0.59, 1, alpha)})`
                                                                            : `translateY(${lerp(-2, 0, alpha)}px) scale(${lerp(0.7, 1, alpha)})`,
                                                                    })),
                                                                    opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.None]: 1,

                                                                        [AnimState.CourtsToSearch]: alpha,
                                                                        [AnimState.CourtsToFilter]: 1 - alpha,
                                                                    })),
                                                                }
                                                                : {},
                                                        }}
                                                    >
                                                        {court.name}
                                                    </animated.div>
                                                </div>

                                                <animated.div
                                                    className="text-block"
                                                    style={{
                                                        ...i === localStore.slider.prev.index
                                                            ? {
                                                                opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.None]: 0,

                                                                    [AnimState.CourtsToSearch]: alpha,
                                                                })),
                                                            }
                                                            : {},
                                                    }}
                                                >
                                                    <div className="text-container">
                                                        <div className="upper-field">
                                                            {court.address}
                                                        </div>
                                                        <div className="lower-field">
                                                            {court.city}, {court.country}
                                                        </div>
                                                    </div>
                                                </animated.div>
                                            </animated.div>
                                        ))
                                        : false}
                            </Slider>
                        </div>
                    )}

                    {!!localStore.slider.next.type && !!localStore.slider.next.items.length && (
                        <div className="slider-next" style={{ zIndex: 2 }} ref={sliderNextRef}>
                            <Slider
                                className={`slider-${localStore.slider.next.type}`}
                                type={localStore.slider.next.type}
                                readonly
                                initialIndex={device.isWideScreen ? (localStore.slider.next.type === 'courts' ? 1 : 2) : 1}
                                onChange={activeIndex => runInAction(() => {
                                    localStore.slider.next.index = activeIndex;
                                    localStore.slider.next.value = localStore.slider.next.items[localStore.slider.next.index];
                                })}
                            >
                                {localStore.slider.next.type === 'players'
                                    ? localStore.slider.next.items.map((player, i) => (
                                        <animated.div
                                            key={player.id}
                                            className="card"
                                            style={{
                                                ...computeSliderNextCardStyle(i),
                                                backgroundImage: `url(${player.image})`,
                                            }}
                                        >
                                            <div className="header-block">
                                                <div className="header-container">
                                                    {player.name}
                                                </div>
                                            </div>

                                            <div className="text-block">
                                                <div className="text-container">
                                                    <div className="upper-field">
                                                        Rating: {player.ratings.ntrp}
                                                    </div>
                                                    <div className="lower-field">
                                                        Games: {player.totalGames}
                                                    </div>
                                                </div>
                                            </div>
                                        </animated.div>
                                    ))
                                    : localStore.slider.next.type === 'courts'
                                        ? localStore.slider.next.items.map((court, i) => (
                                            <animated.div
                                                key={court.id}
                                                className="court-card"
                                                style={{
                                                    ...computeSliderNextCardStyle(i),
                                                    backgroundImage: `url(${court.image})`,
                                                }}
                                            >
                                                <div className="header-block">
                                                    <div className="header-container">
                                                        {court.name}
                                                    </div>
                                                </div>

                                                <div className="text-block">
                                                    <div className="text-container">
                                                        <div className="upper-field">
                                                            {court.address}
                                                        </div>
                                                        <div className="lower-field">
                                                            {court.city}, {court.country}
                                                        </div>
                                                    </div>
                                                </div>
                                            </animated.div>
                                        ))
                                        : false}
                            </Slider>
                        </div>
                    )}

                    {!!localStore.slider.curr.type && !!localStore.slider.curr.itemsRaw.length && (
                        <div className="slider-curr" style={{ zIndex: 3 }} ref={sliderCurrRef}>
                            <Slider
                                className={`slider-${localStore.slider.curr.type}`}
                                type={localStore.slider.curr.type}
                                readonly={localStore.filter.open}
                                initialIndex={localStore.slider.curr.initial !== -1 ? localStore.slider.curr.initial : ((device.isWideScreen ? (localStore.slider.curr.type === 'courts' ? 1 : 2) : 1) + 1)}
                                onInit={(swiper) => {
                                    swiperCurrRef.current = swiper;
                                }}
                                onSwipeUpMove={handleSliderCurrSwipeUp}
                                onSwipeDownMove={handleSliderCurrSwipeDown}
                                onChange={activeIndex => runInAction(() => {
                                    activeIndex -= 1;
                                    localStore.slider.curr.index = activeIndex;
                                    localStore.slider.curr.value = localStore.slider.curr.items[localStore.slider.curr.index];
                                })}
                                onClick={(activeIndex) => {
                                    activeIndex -= 1;
                                    if (localStore.slider.curr.index === activeIndex) {
                                        handleSliderCurrSwipeUp(0, true, true);
                                    }
                                }}
                            >
                                {localStore.slider.curr.type === 'players'
                                    ? ['filter'].concat(localStore.slider.curr.items).map((player: any, i) => (
                                        player === 'filter' ? (
                                            <animated.div
                                                key="filter"
                                                className="card"
                                                style={{
                                                    ...computeSliderCurrCardStyle(i - 1),
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <PlayersFilterCard
                                                    mode={localStore.filter.players.mode}
                                                    onFilter={() => handlePlayersFilter('filter')}
                                                    onSort={() => handlePlayersFilter('sort')}
                                                />
                                            </animated.div>
                                        ) : (
                                            <animated.div
                                                key={player.id}
                                                className="card"
                                                style={{
                                                    ...computeSliderCurrCardStyle(i - 1),
                                                    backgroundImage: `url(${player.image})`,
                                                }}
                                            >
                                                <div className="header-block">
                                                    <animated.div
                                                        className="header-container"
                                                        style={{
                                                            ...((i - 1) === localStore.slider.curr.index)
                                                                ? {
                                                                    color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.SearchToPlayers]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,

                                                                        [AnimState.CourtsToPlayers]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,

                                                                        [AnimState.PLayersPlayersToPlayers]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,
                                                                        [AnimState.PLayersPlayersToPlayersBack]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,
                                                                    })),
                                                                    transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.SearchToPlayers]: `translateY(${lerp(0, -5.5, alpha)}px) scale(${lerp(1, 0.94, alpha)})`,

                                                                        [AnimState.CourtsToPlayers]: `translateY(${lerp(0, -5.5, alpha)}px) scale(${lerp(1, 0.94, alpha)})`,

                                                                        [AnimState.PLayersPlayersToPlayers]: `translateY(${lerp(0, -5.5, alpha)}px) scale(${lerp(1, 0.94, alpha)})`,
                                                                        [AnimState.PLayersPlayersToPlayersBack]: `translateY(${lerp(0, -5.5, alpha)}px) scale(${lerp(1, 0.94, alpha)})`,
                                                                    })),
                                                                }
                                                                : {},
                                                        }}
                                                    >
                                                        {player.name}
                                                    </animated.div>
                                                </div>

                                                <animated.div
                                                    className="text-block"
                                                    style={{
                                                        ...((i - 1) === localStore.slider.curr.index)
                                                            ? {
                                                                font: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                    [AnimState.SearchToPlayers]: `normal normal normal ${lerp(10, 9, alpha)}px/${lerp(10, 9, alpha)}px Copperplate`,

                                                                    [AnimState.CourtsToPlayers]: `normal normal normal ${lerp(10, 9, alpha)}px/${lerp(10, 9, alpha)}px Copperplate`,

                                                                    [AnimState.PLayersPlayersToPlayers]: `normal normal normal ${lerp(10, 9, alpha)}px/${lerp(10, 9, alpha)}px Copperplate`,
                                                                    [AnimState.PLayersPlayersToPlayersBack]: `normal normal normal ${lerp(10, 9, alpha)}px/${lerp(10, 9, alpha)}px Copperplate`,
                                                                })),
                                                            }
                                                            : {},
                                                    }}
                                                >
                                                    <div className="text-container">
                                                        <div className="upper-field">
                                                            Rating: {player.ratings.ntrp}
                                                        </div>
                                                        <div className="lower-field">
                                                            Games: {player.totalGames}
                                                        </div>
                                                    </div>
                                                </animated.div>
                                            </animated.div>
                                        )
                                    ))
                                    : localStore.slider.curr.type === 'courts'
                                        ? [
                                            <animated.div
                                                key="filters"
                                                className="card"
                                                style={{
                                                    ...computeSliderCurrCardStyle(-1),
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <CourtsFilterCard
                                                    mode={localStore.filter.mode}
                                                    onFilter={() => handleCourtsFilter('filter')}
                                                    onSort={() => handleCourtsFilter('sort')}
                                                />
                                            </animated.div>,

                                            ...localStore.slider.curr.items.map((court: any, i) => (
                                                <animated.div
                                                    key={court.id}
                                                    className="court-card"
                                                    style={{
                                                        ...computeSliderCurrCardStyle(i),
                                                        backgroundImage: `url(${court.image})`,
                                                    }}
                                                >
                                                    <div className="header-block">
                                                        <animated.div
                                                            className="header-container"
                                                            style={{
                                                                ...(i === localStore.slider.curr.index)
                                                                    ? {
                                                                        color: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                            [AnimState.SearchToCourts]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,

                                                                            [AnimState.PlayersCourtsToCourts]: `rgb(${lerp(7, 255, alpha)}, ${lerp(44, 255, alpha)}, ${lerp(77, 255, alpha)})`,
                                                                        })),
                                                                        transform: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                            [AnimState.SearchToCourts]: device.isShortScreen
                                                                                ? `translateY(0) scale(${lerp(1, 0.59, alpha)})`
                                                                                : `translateY(${lerp(0, -2, alpha)}px) scale(${lerp(1, 0.7, alpha)})`,

                                                                            [AnimState.PlayersCourtsToCourts]: device.isShortScreen
                                                                                ? `translateY(0) scale(${lerp(1, 0.59, alpha)})`
                                                                                : `translateY(${lerp(0, -2, alpha)}px) scale(${lerp(1, 0.7, alpha)})`,
                                                                        })),
                                                                    }
                                                                    : {},
                                                            }}
                                                        >
                                                            {court.name}
                                                        </animated.div>
                                                    </div>

                                                    <animated.div
                                                        className="text-block"
                                                        style={{
                                                            ...(i === localStore.slider.curr.index)
                                                                ? {
                                                                    opacity: interpolate([anim.alpha], alpha => computeInterpolate({
                                                                        [AnimState.SearchToCourts]: 1 - alpha,

                                                                        [AnimState.PlayersCourtsToCourts]: 1 - alpha,
                                                                    })),
                                                                }
                                                                : {},
                                                        }}
                                                    >
                                                        <div className="text-container">
                                                            <div className="upper-field">
                                                                {court.address}
                                                            </div>
                                                            <div className="lower-field">
                                                                {court.city}, {court.country}
                                                            </div>
                                                        </div>
                                                    </animated.div>
                                                </animated.div>
                                            )),
                                        ]
                                        : false}
                            </Slider>
                        </div>
                    )}
                </div>

                <div
                    className="status-container"
                    onClick={() => handleSliderCurrSwipeDown(0, true, true)}
                >
                    <animated.span
                        className="status-message"
                        style={{
                            position: 'absolute',
                            transform: localStore.slider.curr.type === 'courts' ? 'translateY(-10px)' : undefined,
                            opacity: interpolate([anim.alpha], alpha => (
                                animStateRef.current === AnimState.CourtsToSearch
                                    ? alpha
                                    : 0
                            )),
                        }}
                    >
                        {localStore.slider.prev.type === 'players'
                            ? computeStatusMassage('prev')
                            : <span>{localStore.slider.prev.value?.status}</span>}
                    </animated.span>
                    <animated.span
                        className="status-message"
                        style={{
                            position: 'absolute',
                            transform: localStore.slider.next.type === 'courts' ? 'translateY(-10px)' : undefined,
                            opacity: interpolate([anim.alpha], alpha => (
                                animStateRef.current === AnimState.SearchToCourts
                                || animStateRef.current === AnimState.SearchToPlayers
                                || animStateRef.current === AnimState.CourtsToPlayers
                                    ? alpha
                                    : 0
                            )),
                        }}
                    >
                        {localStore.slider.next.type === 'players'
                            ? computeStatusMassage('next')
                            : <span>{localStore.slider.next.value?.status}</span>}
                    </animated.span>
                    <animated.span
                        className="status-message"
                        style={{
                            transform: localStore.slider.curr.type === 'courts' ? 'translateY(-10px)' : undefined,
                            opacity: interpolate([anim.alpha], alpha => (
                                animStateRef.current === AnimState.SearchToCourts
                                || animStateRef.current === AnimState.SearchToPlayers
                                || animStateRef.current === AnimState.CourtsToSearch
                                || animStateRef.current === AnimState.CourtsToPlayers
                                || animStateRef.current === AnimState.PlayersCourtsToCourts
                                    ? 1 - alpha
                                    : undefined
                            )),
                        }}
                    >
                        {query.calendar && localStore.slider.curr.value?.lastSeen
                            ? <span>Last visit: <strong>{localStore.slider.curr.value.lastSeen}</strong></span>
                            : (localStore.slider.curr.type === 'players'
                                ? computeStatusMassage('curr')
                                : <span>{localStore.slider.curr.value?.status}</span>)}
                    </animated.span>
                </div>

                <Tabs
                    variant={(localStore.filter.open || localStore.filter.players.open) ? 'filters' : params.page}
                    title={{
                        tab1: '',
                        tab2: localStore.slider.curr.type === 'players'
                            ? (authStore.user?.friends.some(f => f.id === localStore.slider.curr.value?.id) ? 'Remove' : 'Add')
                            : localStore.slider.curr.type === 'courts'
                                ? (authStore.user?.courts.some(c => c.id === localStore.slider.curr.value?.id) ? 'Remove' : 'Add')
                                : '',
                        tab3: '',
                    }}
                    onButtonOne={() => {
                        if (localStore.filter.open) {
                            if (params.page === 'search') {
                                animStateRef.current = AnimState.SearchToFilter;
                            } else if (params.page === 'players') {
                                animStateRef.current = AnimState.PlayersCourtsToFilter;
                            }

                            animUpdate(0, false);

                            runInAction(() => {
                                localStore.filter.open = false;
                                localStore.filter.mode = '';

                                localStore.filter.sort.key = '';
                                localStore.filter.sort.order = '';
                            });
                        }

                        if (localStore.filter.players.open) {
                            if (params.page === 'courts') {
                                animStateRef.current = AnimState.CourtsToFilter;
                            } else if (params.page === 'players') {
                                animStateRef.current = AnimState.PlayersPlayersToFilter;
                            }

                            animUpdate(0, false);

                            runInAction(() => {
                                localStore.filter.players.open = false;
                                localStore.filter.players.mode = '';
                                localStore.filter.players.type = query.mode === 'coaches' ? 'coaches' : 'players';

                                localStore.filter.sort.key = '';
                                localStore.filter.sort.order = '';
                            });
                        }
                    }}
                    onButtonTwo={() => {
                        if (localStore.filter.open || localStore.filter.players.open) {
                            if (localStore.filter.open) {
                                localStore.filter.type = ['hard', 'clay', 'grass'];
                            }
                            if (localStore.filter.players.open) {
                                localStore.filter.players = {
                                    open: localStore.filter.players.open,
                                    mode: localStore.filter.players.mode,
                                    type: localStore.filter.players.type,

                                    gender: ['male', 'female'],
                                    rating: {
                                        active: ['age', 'ntrp'],

                                        age: [10, 70],
                                        experience: [0, 30],
                                        ntrp: [1, 7],
                                        ello: [1000, 3000],
                                    },
                                    preference: {
                                        court: ['hard', 'clay', 'grass'],
                                        play: ['single', 'double', 'tournament'],
                                        training: ['individual', 'group', 'professional'],
                                    },
                                    calendar: {
                                        type: '',

                                        schedule: [],
                                        specific: [],
                                    },
                                };
                            }

                            localStore.filter.sort.key = '';
                            localStore.filter.sort.order = '';

                            return;
                        }

                        if (authStore.user && localStore.slider.curr.type === 'players' && localStore.slider.curr.value) {
                            if (authStore.user.friends.some(f => f.id === localStore.slider.curr.value.id)) {
                                if (window.confirm('Remove player from your favorites?')) {
                                    authStore.user.friends = authStore.user.friends.filter(f => f.id !== localStore.slider.curr.value?.id);
                                    UserStore.saveById(authStore.user.id, authStore.user);
                                }
                            } else if (window.confirm('Add player to your favorites?')) {
                                authStore.user.friends.push(localStore.slider.curr.value);
                                UserStore.saveById(authStore.user.id, authStore.user);
                            }
                        }

                        if (authStore.user && localStore.slider.curr.type === 'courts' && localStore.slider.curr.value) {
                            if (authStore.user.courts.some(c => c.id === localStore.slider.curr.value.id)) {
                                if (authStore.user.courts.length <= 1) {
                                    alert('You can\'t delete your main court');
                                } else if (window.confirm('Remove court from your favorites?')) {
                                    authStore.user.courts = authStore.user.courts.filter(f => f.id !== localStore.slider.curr.value?.id);
                                    UserStore.saveById(authStore.user.id, authStore.user);
                                }
                            } else if (window.confirm('Add court to your favorites?')) {
                                authStore.user.courts.push(localStore.slider.curr.value);
                                UserStore.saveById(authStore.user.id, authStore.user);
                            }
                        }
                    }}
                    onButtonThree={() => runInAction(() => {
                        if (localStore.filter.open && localStore.filter.mode === 'filter') {
                            if (localStore.slider.curr.type === 'courts') {
                                localStore.slider.curr.items = localStore.slider.curr.itemsRaw
                                    .filter((item) => {
                                        if (localStore.filter.type.length === 3) {
                                            return true;
                                        }
                                        if (localStore.filter.type.includes('hard') && !!item.type.hard) {
                                            return true;
                                        }
                                        if (localStore.filter.type.includes('clay') && !!item.type.clay) {
                                            return true;
                                        }
                                        if (localStore.filter.type.includes('grass') && !!item.type.grass) {
                                            return true;
                                        }
                                        return false;
                                    });
                            }
                        }

                        if (localStore.filter.players.open && localStore.filter.players.mode === 'filter') {
                            if (localStore.slider.curr.type === 'players') {
                                localStore.slider.curr.items = localStore.slider.curr.itemsRaw
                                    .filter((player) => {
                                        const status = localStore.filter.players.gender.includes(player.gender)
                                            && player.preference.court.some((v: any) => localStore.filter.players.preference.court.includes(v))
                                            && (localStore.filter.players.type === 'players'
                                                ? player.preference.play.some((v: any) => localStore.filter.players.preference.play.includes(v))
                                                : true)
                                            && (localStore.filter.players.type === 'coaches'
                                                ? player.preference.training.some((v: any) => localStore.filter.players.preference.training.includes(v))
                                                : true);
                                        if (!status) {
                                            return false;
                                        }

                                        if (localStore.filter.players.rating.active.includes('age')) {
                                            const age = moment().diff(moment(player.birthday), 'years') || 0;
                                            if (!age
                                                || (age >= localStore.filter.players.rating.age[0]
                                                    && age <= localStore.filter.players.rating.age[1])) {
                                                //
                                            } else {
                                                return false;
                                            }
                                        } else if (localStore.filter.players.rating.active.includes('experience')) {
                                            const experience = player.experience || 0;
                                            if (experience >= localStore.filter.players.rating.experience[0]
                                                && experience <= localStore.filter.players.rating.experience[1]) {
                                                //
                                            } else {
                                                return false;
                                            }
                                        }
                                        if (localStore.filter.players.rating.active.includes('ntrp')) {
                                            const ntrp = player.ratings.ntrp || 0;
                                            if (ntrp >= localStore.filter.players.rating.ntrp[0]
                                                && ntrp <= localStore.filter.players.rating.ntrp[1]) {
                                                //
                                            } else {
                                                return false;
                                            }
                                        } else if (localStore.filter.players.rating.active.includes('ello')) {
                                            const ello = player.ratings.ello || 0;
                                            if (ello >= localStore.filter.players.rating.ello[0]
                                                && ello <= localStore.filter.players.rating.ello[1]) {
                                                //
                                            } else {
                                                return false;
                                            }
                                        }

                                        const playerCalendar = player.calendar;
                                        const filterCalendar = localStore.filter.players.calendar.type
                                            ? localStore.filter.players.calendar[localStore.filter.players.calendar.type]
                                            : [];
                                        if (filterCalendar.length) {
                                            return playerCalendar.some((calendar: any) => {
                                                const date = moment(calendar.date).format('MMM DD');
                                                const time = toJS(calendar.time);

                                                return filterCalendar.find(c => c.date === date && c.time.some(t => time.includes(t)));
                                            });
                                        } else {
                                            return true;
                                        }
                                    });
                            }
                        }

                        /* */

                        if (localStore.filter.open && localStore.filter.mode === 'sort') {
                            if (localStore.slider.curr.type === 'courts') {
                                localStore.slider.curr.items = orderBy(localStore.slider.curr.items, [(item) => {
                                    if (localStore.filter.sort.key === 'players') {
                                        return item.players.filter((p: any) => p.role === 'players').length;
                                    }
                                    if (localStore.filter.sort.key === 'coaches') {
                                        return item.players.filter((p: any) => p.role === 'coaches').length;
                                    }
                                    if (localStore.filter.sort.key === 'courts') {
                                        return 0;
                                    }
                                    if (localStore.filter.sort.key === 'time') {
                                        const timestamp = moment().unix();
                                        const dates = orderBy(item.calendar
                                            .map((c: any) => moment(c.date).unix())
                                            .filter((t: number) => t >= timestamp));
                                        return dates[0] || (timestamp + 1000);
                                    }
                                    return item.id;
                                }], localStore.filter.sort.order === 'asc' ? 'asc' : 'desc');
                            }
                        }

                        if (localStore.filter.players.open && localStore.filter.players.mode === 'sort') {
                            if (localStore.slider.curr.type === 'players') {
                                localStore.slider.curr.items = orderBy(localStore.slider.curr.items, [(item) => {
                                    if (localStore.filter.sort.key === 'age') {
                                        return item.age;
                                    }
                                    if (localStore.filter.sort.key === 'rating') {
                                        return item.ratings.ntrp;
                                    }
                                    if (localStore.filter.sort.key === 'games') {
                                        return item.totalGames;
                                    }
                                    if (localStore.filter.sort.key === 'time') {
                                        const timestamp = moment().unix();
                                        const dates = orderBy(item.calendar
                                            .map((c: any) => moment(c.date).unix())
                                            .filter((t: number) => t >= timestamp));
                                        return dates[0] || (timestamp + 1000);
                                    }
                                    return item.id;
                                }], localStore.filter.sort.order === 'asc' ? 'asc' : 'desc');
                            }
                        }

                        /* */

                        localStore.filter.open = false;
                        localStore.filter.mode = '';

                        localStore.filter.players.open = false;
                        localStore.filter.players.mode = '';
                        localStore.filter.players.type = query.mode === 'coaches' ? 'coaches' : 'players';

                        localStore.slider.curr.initial = 1;

                        process.nextTick(() => {
                            if (params.page === 'search') {
                                animStateRef.current = AnimState.SearchToFilter;
                            } else if (params.page === 'courts') {
                                animStateRef.current = AnimState.CourtsToFilter;
                            } else if (params.page === 'players') {
                                animStateRef.current = AnimState.PlayersPlayersToFilter;
                            } else {
                                return;
                            }

                            animUpdate(0, false);

                            swiperCurrRef.current?.slideTo(1);

                            process.nextTick(() => {
                                localStore.slider.curr.initial = -1;
                            });
                        });
                    })}
                />
            </div>
        </div>
    );
});
